import type { PricingCostComponents, PricingCostRange, PricingLineItem, PricingMultipliers, PricingUnit, ProjectType } from "@reno-estimator/core";
import { allSamplePricingLineItems as v1Items, SAMPLE_DISCLOSURE } from "./sample-line-items.js";

/**
 * Phase 2 pricing version: adds accessibility and permit/design allowance
 * categories (needed by the Phase 2 calculation engine - see
 * packages/core/src/calculation/rules/) across kitchen, bathroom, and
 * basement, on top of everything shipped in the Phase 1
 * "2026.08-sample-1" catalog. Every figure here carries the same
 * "Administrator supplied sample value" disclosure and validation rules
 * as the rest of the sample dataset - see ./README.md.
 *
 * This exists as a SEPARATE version (not an in-place edit of
 * sample-line-items.ts) specifically to give the versioning system and
 * the "pricing version changes" test scenario something real to exercise
 * - see packages/pricing-data/schema/versioning.ts and
 * packages/core/tests/engine-pricing-versions.test.ts.
 */

export const SAMPLE_PRICING_VERSION_V2 = "2026.09-sample-2";
export const SAMPLE_EFFECTIVE_DATE_V2 = "2026-09-01";
const V2_TIMESTAMP = "2026-09-01T00:00:00.000Z";

function restamp(item: PricingLineItem): PricingLineItem {
  return { ...item, pricingVersion: SAMPLE_PRICING_VERSION_V2, effectiveDate: SAMPLE_EFFECTIVE_DATE_V2, modifiedAt: V2_TIMESTAMP };
}

type SampleItemInput = Omit<
  PricingLineItem,
  "pricingVersion" | "effectiveDate" | "sourceId" | "sourceDate" | "confidenceLevel" | "adminNotes" | "createdAt" | "modifiedAt"
> & { adminNotes?: string };

function sampleItemV2(input: SampleItemInput): PricingLineItem {
  return {
    ...input,
    pricingVersion: SAMPLE_PRICING_VERSION_V2,
    effectiveDate: SAMPLE_EFFECTIVE_DATE_V2,
    sourceId: null,
    sourceDate: null,
    confidenceLevel: "administrator_sample_value",
    adminNotes: input.adminNotes ? `${SAMPLE_DISCLOSURE} ${input.adminNotes}` : SAMPLE_DISCLOSURE,
    createdAt: V2_TIMESTAMP,
    modifiedAt: V2_TIMESTAMP
  };
}

const ACCESSIBILITY_COSTS = { low: 1500, typical: 3500, high: 7000, minimum: 800, maximum: 12000 };
const PERMIT_DESIGN_COSTS = { low: 800, typical: 2000, high: 4500, minimum: 400, maximum: 8000 };

export const accessibilitySampleItems: PricingLineItem[] = [
  sampleItemV2({
    id: "kitchen_accessibility",
    projectType: "kitchen",
    category: "accessibility",
    subcategory: null,
    unit: "allowance",
    percentageOfCategory: null,
    costs: ACCESSIBILITY_COSTS,
    components: { labourPercent: 65, materialPercent: 35 },
    multipliers: { complexity: 1.1, finishLevel: {}, regionalOverride: null },
    adminNotes: "Covers items like lowered counters/reach, lever hardware, and clearance adjustments for a kitchen."
  }),
  sampleItemV2({
    id: "bathroom_accessibility",
    projectType: "bathroom",
    category: "accessibility",
    subcategory: null,
    unit: "allowance",
    percentageOfCategory: null,
    costs: ACCESSIBILITY_COSTS,
    components: { labourPercent: 65, materialPercent: 35 },
    multipliers: { complexity: 1.15, finishLevel: {}, regionalOverride: null },
    adminNotes: "Covers items like a curbless/roll-in shower, grab bars, and a comfort-height toilet."
  }),
  sampleItemV2({
    id: "basement_accessibility",
    projectType: "basement",
    category: "accessibility",
    subcategory: null,
    unit: "allowance",
    percentageOfCategory: null,
    costs: ACCESSIBILITY_COSTS,
    components: { labourPercent: 60, materialPercent: 40 },
    multipliers: { complexity: 1.15, finishLevel: {}, regionalOverride: null },
    adminNotes: "Covers items like wider doorways/hallways and lever hardware; does not include a stairlift or exterior ramp."
  }),
  sampleItemV2({
    id: "addition_accessibility",
    projectType: "addition",
    category: "accessibility",
    subcategory: null,
    unit: "allowance",
    percentageOfCategory: null,
    costs: ACCESSIBILITY_COSTS,
    components: { labourPercent: 60, materialPercent: 40 },
    multipliers: { complexity: 1.15, finishLevel: {}, regionalOverride: null },
    adminNotes: "Covers items like wide doorways/hallways, zero-step entry, and lever hardware built into the new addition from the start."
  })
];

export const permitDesignSampleItems: PricingLineItem[] = [
  sampleItemV2({
    id: "kitchen_permit_design",
    projectType: "kitchen",
    category: "permit_design_allowance",
    subcategory: null,
    unit: "allowance",
    percentageOfCategory: null,
    costs: PERMIT_DESIGN_COSTS,
    components: { labourPercent: 10, materialPercent: 90 },
    multipliers: { complexity: 1.0, finishLevel: {}, regionalOverride: null },
    adminNotes: "Covers municipal permit fees and/or design/drafting services if requested; entire cost is classified into the permits/design/misc breakdown bucket."
  }),
  sampleItemV2({
    id: "bathroom_permit_design",
    projectType: "bathroom",
    category: "permit_design_allowance",
    subcategory: null,
    unit: "allowance",
    percentageOfCategory: null,
    costs: { low: 500, typical: 1200, high: 3000, minimum: 300, maximum: 5000 },
    components: { labourPercent: 10, materialPercent: 90 },
    multipliers: { complexity: 1.0, finishLevel: {}, regionalOverride: null }
  }),
  sampleItemV2({
    id: "basement_permit_design",
    projectType: "basement",
    category: "permit_design_allowance",
    subcategory: null,
    unit: "allowance",
    percentageOfCategory: null,
    costs: { low: 1200, typical: 3000, high: 6500, minimum: 600, maximum: 12000 },
    components: { labourPercent: 10, materialPercent: 90 },
    multipliers: { complexity: 1.0, finishLevel: {}, regionalOverride: null },
    adminNotes: "Basement finishing permits are typically higher due to egress/electrical/HVAC inspection requirements."
  })
];

/**
 * Phase 5A: the Kitchen Estimator's fuller line item set. The Phase 0-2
 * kitchen catalog priced a handful of broad categories (one cabinetry
 * item per material choice, one flat electrical/plumbing/structural
 * allowance, etc). Phase 5A's brief asks each of those to be represented
 * by *discrete, independently-priced tiers* rather than a single
 * allowance scaled by a multiplier - e.g. "minor updates" vs "significant
 * changes" for electrical, not one electrical line item nudged up or
 * down. These items add that tiering without touching any Phase 0-2 item
 * still in use as a fallback/mid-tier (see
 * packages/core/src/calculation/rules/kitchen.ts for exactly which
 * existing id plays which tier), so nothing already shipped moves or
 * changes cost. Same disclosure and validation rules as the rest of the
 * sample dataset - see ./README.md.
 */
export const phase5aKitchenSampleItems: PricingLineItem[] = [
  sampleItemV2({
    id: "kitchen_countertop_premium",
    projectType: "kitchen",
    category: "countertops",
    subcategory: "premium_custom",
    unit: "sqft",
    percentageOfCategory: null,
    costs: { low: 130, typical: 190, high: 280, minimum: 110, maximum: 350 },
    components: { labourPercent: 25, materialPercent: 75 },
    multipliers: { complexity: 1.05, finishLevel: { budget: 0.75, standard: 1.0, premium: 1.4, luxury: 2.0 }, regionalOverride: null },
    adminNotes: "Marble, quartzite, waterfall edges, and similarly premium/custom countertop materials."
  }),
  sampleItemV2({
    id: "kitchen_appliances_partial",
    projectType: "kitchen",
    category: "appliances",
    subcategory: "partial",
    unit: "allowance",
    percentageOfCategory: null,
    costs: { low: 1500, typical: 3500, high: 7000, minimum: 1000, maximum: 12000 },
    components: { labourPercent: 10, materialPercent: 90 },
    multipliers: { complexity: 1.0, finishLevel: { budget: 0.7, standard: 1.0, premium: 1.6, luxury: 2.6 }, regionalOverride: null },
    adminNotes: "Represents replacing one or two appliances rather than a full package - see kitchen_appliances_package for a full replacement."
  }),
  sampleItemV2({
    id: "kitchen_appliances_premium",
    projectType: "kitchen",
    category: "appliances",
    subcategory: "premium",
    unit: "allowance",
    percentageOfCategory: null,
    costs: { low: 12000, typical: 22000, high: 45000, minimum: 8000, maximum: 75000 },
    components: { labourPercent: 10, materialPercent: 90 },
    multipliers: { complexity: 1.0, finishLevel: { budget: 0.8, standard: 1.0, premium: 1.3, luxury: 1.8 }, regionalOverride: null },
    adminNotes: "A premium/luxury-brand full appliance package, distinct from applying a luxury finish-level multiplier to the standard package."
  }),
  sampleItemV2({
    id: "kitchen_flooring_basic",
    projectType: "kitchen",
    category: "flooring",
    subcategory: "basic",
    unit: "sqft",
    percentageOfCategory: null,
    costs: { low: 5, typical: 8, high: 12, minimum: 3, maximum: 18 },
    components: { labourPercent: 40, materialPercent: 60 },
    multipliers: { complexity: 1.0, finishLevel: { budget: 0.75, standard: 1.0, premium: 1.4, luxury: 2.0 }, regionalOverride: null }
  }),
  sampleItemV2({
    id: "kitchen_flooring_premium",
    projectType: "kitchen",
    category: "flooring",
    subcategory: "premium",
    unit: "sqft",
    percentageOfCategory: null,
    costs: { low: 18, typical: 28, high: 45, minimum: 14, maximum: 65 },
    components: { labourPercent: 35, materialPercent: 65 },
    multipliers: { complexity: 1.0, finishLevel: { budget: 0.75, standard: 1.0, premium: 1.4, luxury: 2.0 }, regionalOverride: null }
  }),
  sampleItemV2({
    id: "kitchen_backsplash_full_height",
    projectType: "kitchen",
    category: "backsplash",
    subcategory: "full_height",
    unit: "sqft",
    percentageOfCategory: null,
    costs: { low: 20, typical: 35, high: 60, minimum: 15, maximum: 90 },
    components: { labourPercent: 55, materialPercent: 45 },
    multipliers: { complexity: 1.05, finishLevel: { budget: 0.75, standard: 1.0, premium: 1.4, luxury: 2.0 }, regionalOverride: null },
    adminNotes: "Full-height (countertop to upper cabinets or ceiling), priced per sqft over a larger area than the standard backsplash band."
  }),
  sampleItemV2({
    id: "kitchen_backsplash_premium",
    projectType: "kitchen",
    category: "backsplash",
    subcategory: "premium",
    unit: "sqft",
    percentageOfCategory: null,
    costs: { low: 30, typical: 55, high: 95, minimum: 22, maximum: 140 },
    components: { labourPercent: 50, materialPercent: 50 },
    multipliers: { complexity: 1.05, finishLevel: { budget: 0.75, standard: 1.0, premium: 1.4, luxury: 2.0 }, regionalOverride: null },
    adminNotes: "Premium/custom material or installation (e.g. mosaic, natural stone, mitred edges) at the standard backsplash band area."
  }),
  sampleItemV2({
    id: "kitchen_island_large",
    projectType: "kitchen",
    category: "island",
    subcategory: "large_custom",
    unit: "item",
    percentageOfCategory: null,
    costs: { low: 6000, typical: 11000, high: 18000, minimum: 4000, maximum: 28000 },
    components: { labourPercent: 35, materialPercent: 65 },
    multipliers: { complexity: 1.1, finishLevel: { budget: 0.85, standard: 1.0, premium: 1.3, luxury: 1.85 }, regionalOverride: null }
  }),
  sampleItemV2({
    id: "kitchen_electrical_minor",
    projectType: "kitchen",
    category: "electrical",
    subcategory: "minor_updates",
    unit: "allowance",
    percentageOfCategory: null,
    costs: { low: 400, typical: 900, high: 1800, minimum: 250, maximum: 3000 },
    components: { labourPercent: 75, materialPercent: 25 },
    multipliers: { complexity: 1.0, finishLevel: { budget: 0.9, standard: 1.0, premium: 1.2, luxury: 1.5 }, regionalOverride: null },
    adminNotes: "E.g. swapping fixtures/switches on existing circuits - no new circuits or panel work."
  }),
  sampleItemV2({
    id: "kitchen_electrical_lighting",
    projectType: "kitchen",
    category: "electrical",
    subcategory: "new_lighting",
    unit: "allowance",
    percentageOfCategory: null,
    costs: { low: 800, typical: 1600, high: 3000, minimum: 500, maximum: 5000 },
    components: { labourPercent: 70, materialPercent: 30 },
    multipliers: { complexity: 1.05, finishLevel: { budget: 0.85, standard: 1.0, premium: 1.3, luxury: 1.7 }, regionalOverride: null }
  }),
  sampleItemV2({
    id: "kitchen_electrical_circuits",
    projectType: "kitchen",
    category: "electrical",
    subcategory: "additional_circuits",
    unit: "allowance",
    percentageOfCategory: null,
    costs: { low: 1000, typical: 2000, high: 3800, minimum: 600, maximum: 6500 },
    components: { labourPercent: 75, materialPercent: 25 },
    multipliers: { complexity: 1.1, finishLevel: { budget: 0.85, standard: 1.0, premium: 1.25, luxury: 1.6 }, regionalOverride: null },
    adminNotes: "Adding outlets/dedicated circuits (e.g. for a range or an island) - see kitchen_electrical for a full rewire/panel-level scope."
  }),
  sampleItemV2({
    id: "kitchen_plumbing_minor",
    projectType: "kitchen",
    category: "plumbing",
    subcategory: "minor_changes",
    unit: "allowance",
    percentageOfCategory: null,
    costs: { low: 400, typical: 900, high: 1800, minimum: 250, maximum: 3000 },
    components: { labourPercent: 70, materialPercent: 30 },
    multipliers: { complexity: 1.05, finishLevel: { budget: 0.9, standard: 1.0, premium: 1.2, luxury: 1.5 }, regionalOverride: null }
  }),
  sampleItemV2({
    id: "kitchen_plumbing_relocation",
    projectType: "kitchen",
    category: "plumbing",
    subcategory: "fixtures_relocated",
    unit: "allowance",
    percentageOfCategory: null,
    costs: { low: 700, typical: 1600, high: 3200, minimum: 450, maximum: 5500 },
    components: { labourPercent: 68, materialPercent: 32 },
    multipliers: { complexity: 1.1, finishLevel: { budget: 0.88, standard: 1.0, premium: 1.2, luxury: 1.5 }, regionalOverride: null },
    adminNotes: "Moving a sink/dishwasher/gas line to a new location - new rough-in required, but short of a full kitchen replumb."
  }),
  sampleItemV2({
    id: "kitchen_structural_non_load_bearing",
    projectType: "kitchen",
    category: "structural_changes",
    subcategory: "non_structural_wall_removal",
    unit: "allowance",
    percentageOfCategory: null,
    costs: { low: 800, typical: 2000, high: 4500, minimum: 500, maximum: 8000 },
    components: { labourPercent: 65, materialPercent: 35 },
    multipliers: { complexity: 1.05, finishLevel: {}, regionalOverride: null },
    adminNotes: "A confirmed non-load-bearing (partition) wall - no engineering assessment expected, but see kitchen_structural_changes/kitchen_structural_major for any wall whose load-bearing status isn't confirmed."
  }),
  sampleItemV2({
    id: "kitchen_structural_major",
    projectType: "kitchen",
    category: "structural_changes",
    subcategory: "major_layout_changes",
    unit: "allowance",
    percentageOfCategory: null,
    costs: { low: 5000, typical: 12000, high: 28000, minimum: 3000, maximum: 55000 },
    components: { labourPercent: 55, materialPercent: 45 },
    multipliers: { complexity: 1.4, finishLevel: {}, regionalOverride: null },
    adminNotes: "Major layout changes (multiple walls, structural beam/post work). Wide range reflects real site-to-site variability - always requires an engineer's assessment before permitting."
  }),
  sampleItemV2({
    id: "kitchen_painting_walls",
    projectType: "kitchen",
    category: "painting",
    subcategory: "walls_only",
    unit: "sqft",
    percentageOfCategory: null,
    costs: { low: 2, typical: 4, high: 7, minimum: 1.5, maximum: 11 },
    components: { labourPercent: 75, materialPercent: 25 },
    multipliers: { complexity: 1.0, finishLevel: { budget: 0.85, standard: 1.0, premium: 1.3, luxury: 1.7 }, regionalOverride: null }
  }),
  sampleItemV2({
    id: "kitchen_painting_full",
    projectType: "kitchen",
    category: "painting",
    subcategory: "full_room",
    unit: "sqft",
    percentageOfCategory: null,
    costs: { low: 4, typical: 7, high: 12, minimum: 3, maximum: 18 },
    components: { labourPercent: 70, materialPercent: 30 },
    multipliers: { complexity: 1.0, finishLevel: { budget: 0.85, standard: 1.0, premium: 1.3, luxury: 1.7 }, regionalOverride: null },
    adminNotes: "Walls, ceiling, and trim."
  })
];

// ---------------------------------------------------------------------------
// Phase 8: Basement Estimator tiered categories
// ---------------------------------------------------------------------------

const STANDARD_FINISH_MULTIPLIERS_V2 = { budget: 0.75, standard: 1.0, premium: 1.4, luxury: 2.0 };

/**
 * New basement pricing categories added for Phase 8, so the basement
 * calculator (packages/core/src/calculation/rules/basement.ts) can price
 * each scope addition from the brief as its own discrete line item
 * rather than one flat sqft multiplier - "do not simply calculate square
 * footage and apply one multiplier." Every figure carries the same
 * "Administrator supplied sample value" disclosure as the rest of the
 * sample dataset - see ./README.md.
 */
export const phase8BasementSampleItems: PricingLineItem[] = [
  sampleItemV2({
    id: "basement_bathroom_renovation",
    projectType: "basement",
    category: "bathroom",
    subcategory: "existing_renovation",
    unit: "room",
    percentageOfCategory: null,
    costs: { low: 5000, typical: 9000, high: 15000, minimum: 3500, maximum: 22000 },
    components: { labourPercent: 60, materialPercent: 40 },
    multipliers: { complexity: 1.1, finishLevel: STANDARD_FINISH_MULTIPLIERS_V2, regionalOverride: null },
    adminNotes: "Renovating an existing basement bathroom (rough-in already present) - materially cheaper than adding a new one; see basement_bathroom_addition."
  }),
  sampleItemV2({
    id: "basement_kitchenette",
    projectType: "basement",
    category: "kitchen_wet_bar",
    subcategory: "kitchenette",
    unit: "allowance",
    percentageOfCategory: null,
    costs: { low: 8000, typical: 14000, high: 22000, minimum: 5000, maximum: 32000 },
    components: { labourPercent: 45, materialPercent: 55 },
    multipliers: { complexity: 1.15, finishLevel: { budget: 0.75, standard: 1.0, premium: 1.5, luxury: 2.3 }, regionalOverride: null },
    adminNotes: "Compact cooking setup (e.g. mini fridge, microwave/hot plate, small counter) - between a wet bar and a full kitchen in scope."
  }),
  sampleItemV2({
    id: "basement_full_kitchen",
    projectType: "basement",
    category: "kitchen_wet_bar",
    subcategory: "full_kitchen",
    unit: "allowance",
    percentageOfCategory: null,
    costs: { low: 18000, typical: 30000, high: 48000, minimum: 12000, maximum: 70000 },
    components: { labourPercent: 45, materialPercent: 55 },
    multipliers: { complexity: 1.2, finishLevel: { budget: 0.75, standard: 1.0, premium: 1.5, luxury: 2.3 }, regionalOverride: null },
    adminNotes: "A complete second kitchen - full-size appliances and cabinetry. Commonly paired with a secondary suite/legal basement apartment, which has its own plumbing/electrical/permit implications beyond this allowance."
  }),
  sampleItemV2({
    id: "basement_office_buildout",
    projectType: "basement",
    category: "office",
    subcategory: null,
    unit: "allowance",
    percentageOfCategory: null,
    costs: { low: 2000, typical: 4000, high: 7000, minimum: 1200, maximum: 12000 },
    components: { labourPercent: 55, materialPercent: 45 },
    multipliers: { complexity: 1.0, finishLevel: STANDARD_FINISH_MULTIPLIERS_V2, regionalOverride: null },
    adminNotes: "Beyond the base finishing already applied to the space: extra outlets/data wiring, built-in desk/shelving allowance."
  }),
  sampleItemV2({
    id: "basement_plumbing",
    projectType: "basement",
    category: "plumbing",
    subcategory: null,
    unit: "allowance",
    percentageOfCategory: null,
    costs: { low: 1200, typical: 2800, high: 5500, minimum: 700, maximum: 9000 },
    components: { labourPercent: 70, materialPercent: 30 },
    multipliers: { complexity: 1.05, finishLevel: { budget: 0.9, standard: 1.0, premium: 1.2, luxury: 1.5 }, regionalOverride: null },
    adminNotes: "General plumbing changes not already captured by a bathroom or kitchen/wet-bar addition (e.g. a laundry rough-in, a floor drain relocation)."
  }),
  sampleItemV2({
    id: "basement_hvac_extend",
    projectType: "basement",
    category: "hvac",
    subcategory: "extend_existing",
    unit: "allowance",
    percentageOfCategory: null,
    costs: { low: 1000, typical: 2500, high: 5000, minimum: 600, maximum: 8000 },
    components: { labourPercent: 65, materialPercent: 35 },
    multipliers: { complexity: 1.05, finishLevel: {}, regionalOverride: null },
    adminNotes: "Adding ductwork/registers or a supplemental unit off the existing system - see basement_hvac for a dedicated zone/standalone system."
  }),
  sampleItemV2({
    id: "basement_ceiling_drop",
    projectType: "basement",
    category: "ceiling",
    subcategory: "drop_ceiling",
    unit: "sqft",
    percentageOfCategory: null,
    costs: { low: 4, typical: 7, high: 12, minimum: 3, maximum: 18 },
    components: { labourPercent: 55, materialPercent: 45 },
    multipliers: { complexity: 1.0, finishLevel: { budget: 0.85, standard: 1.0, premium: 1.3, luxury: 1.7 }, regionalOverride: null },
    adminNotes: "Drop/suspended ceiling tile system - typically costs more than drywall but keeps ductwork/wiring accessible; see basement_ceiling for drywall."
  }),
  sampleItemV2({
    id: "basement_ceiling_exposed",
    projectType: "basement",
    category: "ceiling",
    subcategory: "exposed_finished",
    unit: "sqft",
    percentageOfCategory: null,
    costs: { low: 1.5, typical: 2.5, high: 4.5, minimum: 1, maximum: 7 },
    components: { labourPercent: 80, materialPercent: 20 },
    multipliers: { complexity: 1.0, finishLevel: { budget: 0.85, standard: 1.0, premium: 1.3, luxury: 1.8 }, regionalOverride: null },
    adminNotes: "Painted/finished exposed joists and ductwork rather than a covered ceiling - mostly labour (paint, minor cleanup of the joist bay), no ceiling material."
  }),
  sampleItemV2({
    id: "basement_flooring_basic",
    projectType: "basement",
    category: "flooring",
    subcategory: "basic",
    unit: "sqft",
    percentageOfCategory: null,
    costs: { low: 4, typical: 6, high: 9, minimum: 3, maximum: 13 },
    components: { labourPercent: 40, materialPercent: 60 },
    multipliers: { complexity: 1.0, finishLevel: { budget: 0.75, standard: 1.0, premium: 1.4, luxury: 2.0 }, regionalOverride: null }
  }),
  sampleItemV2({
    id: "basement_flooring_premium",
    projectType: "basement",
    category: "flooring",
    subcategory: "premium",
    unit: "sqft",
    percentageOfCategory: null,
    costs: { low: 14, typical: 22, high: 35, minimum: 10, maximum: 50 },
    components: { labourPercent: 35, materialPercent: 65 },
    multipliers: { complexity: 1.0, finishLevel: { budget: 0.75, standard: 1.0, premium: 1.4, luxury: 2.0 }, regionalOverride: null }
  }),
  sampleItemV2({
    id: "basement_fireplace",
    projectType: "basement",
    category: "fireplace",
    subcategory: null,
    unit: "item",
    percentageOfCategory: null,
    costs: { low: 3500, typical: 6500, high: 12000, minimum: 2500, maximum: 20000 },
    components: { labourPercent: 45, materialPercent: 55 },
    multipliers: { complexity: 1.1, finishLevel: { budget: 0.8, standard: 1.0, premium: 1.4, luxury: 2.2 }, regionalOverride: null },
    adminNotes: "Electric or vented-gas fireplace unit and surround, installed. A wood-burning/masonry fireplace addition would need a structural/venting assessment beyond this allowance."
  }),
  sampleItemV2({
    id: "basement_built_ins",
    projectType: "basement",
    category: "built_ins",
    subcategory: null,
    unit: "allowance",
    percentageOfCategory: null,
    costs: { low: 1500, typical: 3500, high: 7000, minimum: 1000, maximum: 12000 },
    components: { labourPercent: 40, materialPercent: 60 },
    multipliers: { complexity: 1.0, finishLevel: { budget: 0.8, standard: 1.0, premium: 1.4, luxury: 2.0 }, regionalOverride: null }
  }),
  sampleItemV2({
    id: "basement_soundproofing",
    projectType: "basement",
    category: "soundproofing",
    subcategory: null,
    unit: "sqft",
    percentageOfCategory: null,
    costs: { low: 2, typical: 4, high: 7, minimum: 1.5, maximum: 11 },
    components: { labourPercent: 60, materialPercent: 40 },
    multipliers: { complexity: 1.0, finishLevel: {}, regionalOverride: null },
    adminNotes: "Resilient channel/sound-isolation clips and insulation added to ceiling and/or walls - priced per sqft of treated area (approximated from room size)."
  })
];

// ---------------------------------------------------------------------------
// Phase 12: Home Addition Estimator
// ---------------------------------------------------------------------------

/**
 * New pricing categories for Phase 12's Home Addition Estimator (see
 * packages/core/src/calculation/rules/addition.ts and
 * packages/config/src/questions/addition.ts). Per the brief, an addition
 * is treated as a HIGH-COMPLEXITY project type: it is priced as many
 * independent line items (shell construction, foundation, structural
 * complexity, each mechanical system, envelope/exterior, design,
 * engineering, permits) rather than one sqft multiplier, mirroring the
 * tiered pattern kitchen (Phase 5A) and basement (Phase 8) established.
 * Every figure carries the same "Administrator supplied sample value"
 * disclosure and validation rules as the rest of the sample dataset -
 * see ./README.md. Costs are deliberately at the higher end relative to
 * an interior remodel of the same square footage, since an addition also
 * includes exterior envelope, foundation, and roofing work a kitchen or
 * basement remodel does not.
 */
const ADDITION_FINISH_MULTIPLIERS_V2 = { budget: 0.75, standard: 1.0, premium: 1.45, luxury: 2.1 };

export const phase12AdditionSampleItems: PricingLineItem[] = [
  sampleItemV2({
    id: "addition_base_construction",
    projectType: "addition",
    category: "base_construction",
    subcategory: null,
    unit: "sqft",
    percentageOfCategory: null,
    costs: { low: 220, typical: 320, high: 480, minimum: 180, maximum: 650 },
    components: { labourPercent: 45, materialPercent: 55 },
    multipliers: { complexity: 1.0, finishLevel: ADDITION_FINISH_MULTIPLIERS_V2, regionalOverride: null },
    adminNotes: "Core shell construction (footings/framing/envelope/insulation/drywall shell) before foundation type, systems, or finishes below are added - a true ground-up build, not a remodel, so per-sqft costs run well above an interior renovation of the same size."
  }),
  sampleItemV2({
    id: "addition_second_storey_premium",
    projectType: "addition",
    category: "structural_complexity",
    subcategory: "second_storey_addition",
    unit: "percentage",
    percentageOfCategory: "base_construction",
    costs: { low: 15, typical: 25, high: 40, minimum: 10, maximum: 55 },
    components: { labourPercent: 70, materialPercent: 30 },
    multipliers: { complexity: 1.0, finishLevel: {}, regionalOverride: null },
    adminNotes: "Building a new storey over existing structure requires assessing/reinforcing the existing floor and roof structure - priced as a percentage premium on top of base construction."
  }),
  sampleItemV2({
    id: "addition_foundation_slab",
    projectType: "addition",
    category: "foundation",
    subcategory: "slab_on_grade",
    unit: "sqft",
    percentageOfCategory: null,
    costs: { low: 9, typical: 15, high: 24, minimum: 6, maximum: 35 },
    components: { labourPercent: 40, materialPercent: 60 },
    multipliers: { complexity: 1.0, finishLevel: {}, regionalOverride: null }
  }),
  sampleItemV2({
    id: "addition_foundation_crawl_space",
    projectType: "addition",
    category: "foundation",
    subcategory: "crawl_space",
    unit: "sqft",
    percentageOfCategory: null,
    costs: { low: 15, typical: 24, high: 36, minimum: 10, maximum: 50 },
    components: { labourPercent: 45, materialPercent: 55 },
    multipliers: { complexity: 1.0, finishLevel: {}, regionalOverride: null }
  }),
  sampleItemV2({
    id: "addition_foundation_full_basement",
    projectType: "addition",
    category: "foundation",
    subcategory: "full_foundation_basement",
    unit: "sqft",
    percentageOfCategory: null,
    costs: { low: 38, typical: 58, high: 90, minimum: 28, maximum: 130 },
    components: { labourPercent: 40, materialPercent: 60 },
    multipliers: { complexity: 1.05, finishLevel: {}, regionalOverride: null },
    adminNotes: "A full-depth poured foundation under the addition footprint - excavation, footings, and foundation walls; does not include finishing the resulting basement space."
  }),
  sampleItemV2({
    id: "addition_kitchen_renovate_existing",
    projectType: "addition",
    category: "kitchen",
    subcategory: "renovate_existing",
    unit: "room",
    percentageOfCategory: null,
    costs: { low: 6000, typical: 11000, high: 18000, minimum: 4000, maximum: 26000 },
    components: { labourPercent: 45, materialPercent: 55 },
    multipliers: { complexity: 1.1, finishLevel: ADDITION_FINISH_MULTIPLIERS_V2, regionalOverride: null },
    adminNotes: "Extending or reworking an existing kitchen that borders the addition, rather than building an entirely new one."
  }),
  sampleItemV2({
    id: "addition_kitchen_new",
    projectType: "addition",
    category: "kitchen",
    subcategory: "new_kitchen",
    unit: "room",
    percentageOfCategory: null,
    costs: { low: 22000, typical: 36000, high: 58000, minimum: 15000, maximum: 85000 },
    components: { labourPercent: 40, materialPercent: 60 },
    multipliers: { complexity: 1.15, finishLevel: ADDITION_FINISH_MULTIPLIERS_V2, regionalOverride: null },
    adminNotes: "A complete new kitchen within the addition - cabinetry, countertops, and appliances at the selected finish level."
  }),
  sampleItemV2({
    id: "addition_bathroom_one",
    projectType: "addition",
    category: "bathroom",
    subcategory: "one_bathroom",
    unit: "room",
    percentageOfCategory: null,
    costs: { low: 10000, typical: 17000, high: 27000, minimum: 7000, maximum: 38000 },
    components: { labourPercent: 55, materialPercent: 45 },
    multipliers: { complexity: 1.15, finishLevel: ADDITION_FINISH_MULTIPLIERS_V2, regionalOverride: null },
    adminNotes: "A brand-new bathroom with new supply/drain runs - materially more than renovating an existing one, since there's no existing rough-in to build from."
  }),
  sampleItemV2({
    id: "addition_bathroom_multiple",
    projectType: "addition",
    category: "bathroom",
    subcategory: "multiple_bathrooms",
    unit: "room",
    percentageOfCategory: null,
    costs: { low: 9000, typical: 15500, high: 25000, minimum: 6500, maximum: 34000 },
    components: { labourPercent: 55, materialPercent: 45 },
    multipliers: { complexity: 1.15, finishLevel: ADDITION_FINISH_MULTIPLIERS_V2, regionalOverride: null },
    adminNotes: "Priced per bathroom when the addition includes two or more; the quantity applied is an assumed count of 2 unless a specific number of bathrooms is captured in a future phase - see the corresponding assumption in rules/addition.ts."
  }),
  sampleItemV2({
    id: "addition_bedroom",
    projectType: "addition",
    category: "bedroom",
    subcategory: null,
    unit: "room",
    percentageOfCategory: null,
    costs: { low: 2200, typical: 3800, high: 6200, minimum: 1500, maximum: 9500 },
    components: { labourPercent: 50, materialPercent: 50 },
    multipliers: { complexity: 1.0, finishLevel: ADDITION_FINISH_MULTIPLIERS_V2, regionalOverride: null },
    adminNotes: "Interior finishing specific to each bedroom (closet, doors, trim) beyond the base shell construction already priced above - priced per bedroom."
  }),
  sampleItemV2({
    id: "addition_structural_standard",
    projectType: "addition",
    category: "structural_complexity",
    subcategory: "standard",
    unit: "allowance",
    percentageOfCategory: null,
    costs: { low: 1500, typical: 3000, high: 5500, minimum: 800, maximum: 9000 },
    components: { labourPercent: 75, materialPercent: 25 },
    multipliers: { complexity: 1.0, finishLevel: {}, regionalOverride: null },
    adminNotes: "Extra framing labour/hardware for a straightforward roof/wall tie-in to the existing home."
  }),
  sampleItemV2({
    id: "addition_structural_moderate",
    projectType: "addition",
    category: "structural_complexity",
    subcategory: "moderate",
    unit: "allowance",
    percentageOfCategory: null,
    // Phase 20 audit fix: raised from the original 8500 typical - at a typical addition's
    // overall project size (well into six figures), the old standard->moderate delta was
    // small enough to vanish entirely under the $10,000 rounding increment that applies
    // above $200,000 (see rounding.ts), so "moderate" and "standard" could round to an
    // identical headline estimate despite genuinely different underlying costs.
    costs: { low: 9000, typical: 16000, high: 26000, minimum: 5000, maximum: 38000 },
    components: { labourPercent: 75, materialPercent: 25 },
    multipliers: { complexity: 1.0, finishLevel: {}, regionalOverride: null },
    adminNotes: "Roofline matching, a load-bearing wall opening, or a modest structural tie-in beyond a simple box addition."
  }),
  sampleItemV2({
    id: "addition_structural_complex",
    projectType: "addition",
    category: "structural_complexity",
    subcategory: "complex",
    unit: "allowance",
    percentageOfCategory: null,
    // Phase 20 audit fix - see "moderate" above for why this was raised.
    costs: { low: 16000, typical: 29000, high: 46000, minimum: 9000, maximum: 65000 },
    components: { labourPercent: 75, materialPercent: 25 },
    multipliers: { complexity: 1.0, finishLevel: {}, regionalOverride: null },
    adminNotes: "Major structural changes - removing exterior load-bearing walls, cantilevers, complex roofline matching, or building over existing structure."
  }),
  sampleItemV2({
    id: "addition_hvac_extend",
    projectType: "addition",
    category: "hvac",
    subcategory: "extend_existing",
    unit: "allowance",
    percentageOfCategory: null,
    costs: { low: 2500, typical: 5000, high: 9000, minimum: 1500, maximum: 14000 },
    components: { labourPercent: 65, materialPercent: 35 },
    multipliers: { complexity: 1.05, finishLevel: {}, regionalOverride: null }
  }),
  sampleItemV2({
    id: "addition_hvac_dedicated",
    projectType: "addition",
    category: "hvac",
    subcategory: "dedicated_system",
    unit: "allowance",
    percentageOfCategory: null,
    costs: { low: 6500, typical: 11500, high: 19000, minimum: 4000, maximum: 28000 },
    components: { labourPercent: 60, materialPercent: 40 },
    multipliers: { complexity: 1.1, finishLevel: {}, regionalOverride: null },
    adminNotes: "A dedicated zone, ductless mini-split, or standalone system sized for the addition rather than tying into the existing furnace/AC."
  }),
  sampleItemV2({
    id: "addition_electrical_standard",
    projectType: "addition",
    category: "electrical",
    subcategory: "standard_wiring",
    unit: "allowance",
    percentageOfCategory: null,
    costs: { low: 2500, typical: 5000, high: 9000, minimum: 1500, maximum: 14000 },
    components: { labourPercent: 70, materialPercent: 30 },
    multipliers: { complexity: 1.05, finishLevel: {}, regionalOverride: null }
  }),
  sampleItemV2({
    id: "addition_electrical_panel_upgrade",
    projectType: "addition",
    category: "electrical",
    subcategory: "panel_upgrade_required",
    unit: "allowance",
    percentageOfCategory: null,
    costs: { low: 4000, typical: 7000, high: 12000, minimum: 2500, maximum: 18000 },
    components: { labourPercent: 65, materialPercent: 35 },
    multipliers: { complexity: 1.1, finishLevel: {}, regionalOverride: null },
    adminNotes: "Includes a main electrical panel/service upgrade in addition to the addition's own new wiring."
  }),
  sampleItemV2({
    id: "addition_plumbing_extend",
    projectType: "addition",
    category: "plumbing",
    subcategory: "extend_existing_lines",
    unit: "allowance",
    percentageOfCategory: null,
    costs: { low: 2000, typical: 4500, high: 8000, minimum: 1200, maximum: 13000 },
    components: { labourPercent: 70, materialPercent: 30 },
    multipliers: { complexity: 1.05, finishLevel: {}, regionalOverride: null }
  }),
  sampleItemV2({
    id: "addition_plumbing_new_lines",
    projectType: "addition",
    category: "plumbing",
    subcategory: "new_dedicated_lines",
    unit: "allowance",
    percentageOfCategory: null,
    costs: { low: 4500, typical: 9000, high: 16000, minimum: 3000, maximum: 24000 },
    components: { labourPercent: 70, materialPercent: 30 },
    multipliers: { complexity: 1.1, finishLevel: {}, regionalOverride: null },
    adminNotes: "New dedicated supply/drain runs, possibly including a new stack - materially more involved than extending existing lines."
  }),
  sampleItemV2({
    id: "addition_windows_doors_standard",
    projectType: "addition",
    category: "windows_doors",
    subcategory: "standard",
    unit: "sqft",
    percentageOfCategory: null,
    costs: { low: 8, typical: 14, high: 22, minimum: 5, maximum: 32 },
    components: { labourPercent: 35, materialPercent: 65 },
    multipliers: { complexity: 1.0, finishLevel: {}, regionalOverride: null },
    adminNotes: "Priced per sqft of the addition's footprint as a planning-level proxy for window/door area, not the glazed area itself."
  }),
  sampleItemV2({
    id: "addition_windows_doors_upgraded",
    projectType: "addition",
    category: "windows_doors",
    subcategory: "upgraded",
    unit: "sqft",
    percentageOfCategory: null,
    costs: { low: 14, typical: 24, high: 38, minimum: 9, maximum: 55 },
    components: { labourPercent: 35, materialPercent: 65 },
    multipliers: { complexity: 1.0, finishLevel: {}, regionalOverride: null }
  }),
  sampleItemV2({
    id: "addition_windows_doors_premium",
    projectType: "addition",
    category: "windows_doors",
    subcategory: "premium",
    unit: "sqft",
    percentageOfCategory: null,
    costs: { low: 24, typical: 40, high: 65, minimum: 16, maximum: 95 },
    components: { labourPercent: 30, materialPercent: 70 },
    multipliers: { complexity: 1.0, finishLevel: {}, regionalOverride: null },
    adminNotes: "Large-format/specialty windows, sliding or folding glass doors, and premium hardware."
  }),
  sampleItemV2({
    id: "addition_roofing_tie_in",
    projectType: "addition",
    category: "roofing",
    subcategory: "tie_into_existing_roofline",
    unit: "allowance",
    percentageOfCategory: null,
    costs: { low: 4500, typical: 8500, high: 15000, minimum: 3000, maximum: 22000 },
    components: { labourPercent: 60, materialPercent: 40 },
    multipliers: { complexity: 1.05, finishLevel: {}, regionalOverride: null },
    adminNotes: "Matching and flashing into the existing roofline - complexity/cost depends heavily on the existing roof's pitch and material, which cannot be assessed from a questionnaire."
  }),
  sampleItemV2({
    id: "addition_roofing_new_section",
    projectType: "addition",
    category: "roofing",
    subcategory: "new_roof_section",
    unit: "sqft",
    percentageOfCategory: null,
    costs: { low: 7, typical: 11, high: 17, minimum: 5, maximum: 24 },
    components: { labourPercent: 55, materialPercent: 45 },
    multipliers: { complexity: 1.0, finishLevel: {}, regionalOverride: null }
  }),
  sampleItemV2({
    id: "addition_roofing_flat",
    projectType: "addition",
    category: "roofing",
    subcategory: "flat_roof",
    unit: "sqft",
    percentageOfCategory: null,
    costs: { low: 9, typical: 14, high: 21, minimum: 6, maximum: 30 },
    components: { labourPercent: 50, materialPercent: 50 },
    multipliers: { complexity: 1.0, finishLevel: {}, regionalOverride: null }
  }),
  sampleItemV2({
    id: "addition_exterior_finish_match",
    projectType: "addition",
    category: "exterior_finish",
    subcategory: "match_existing",
    unit: "sqft",
    percentageOfCategory: null,
    costs: { low: 11, typical: 18, high: 27, minimum: 7, maximum: 38 },
    components: { labourPercent: 45, materialPercent: 55 },
    multipliers: { complexity: 1.1, finishLevel: {}, regionalOverride: null },
    adminNotes: "Matching an existing exterior (siding, brick, or stone) typically costs more than a fresh install of the same material, since it usually requires sourcing a close match and blending the transition."
  }),
  sampleItemV2({
    id: "addition_exterior_finish_vinyl",
    projectType: "addition",
    category: "exterior_finish",
    subcategory: "vinyl_siding",
    unit: "sqft",
    percentageOfCategory: null,
    costs: { low: 6, typical: 10, high: 15, minimum: 4, maximum: 21 },
    components: { labourPercent: 40, materialPercent: 60 },
    multipliers: { complexity: 1.0, finishLevel: {}, regionalOverride: null }
  }),
  sampleItemV2({
    id: "addition_exterior_finish_brick_stone",
    projectType: "addition",
    category: "exterior_finish",
    subcategory: "brick_stone",
    unit: "sqft",
    percentageOfCategory: null,
    costs: { low: 19, typical: 29, high: 44, minimum: 13, maximum: 62 },
    components: { labourPercent: 45, materialPercent: 55 },
    multipliers: { complexity: 1.05, finishLevel: {}, regionalOverride: null }
  }),
  sampleItemV2({
    id: "addition_exterior_finish_premium",
    projectType: "addition",
    category: "exterior_finish",
    subcategory: "premium_finish",
    unit: "sqft",
    percentageOfCategory: null,
    costs: { low: 26, typical: 40, high: 60, minimum: 18, maximum: 85 },
    components: { labourPercent: 40, materialPercent: 60 },
    multipliers: { complexity: 1.0, finishLevel: {}, regionalOverride: null },
    adminNotes: "E.g. fibre cement panel systems, natural wood siding, or a mixed high-end exterior treatment."
  }),
  sampleItemV2({
    id: "addition_design_architectural",
    projectType: "addition",
    category: "permit_design_allowance",
    subcategory: "design_architectural",
    unit: "allowance",
    percentageOfCategory: null,
    costs: { low: 3000, typical: 6500, high: 13000, minimum: 1800, maximum: 22000 },
    components: { labourPercent: 15, materialPercent: 85 },
    multipliers: { complexity: 1.0, finishLevel: {}, regionalOverride: null },
    adminNotes: "Design/architectural drawings sufficient for a typical municipal permit application - actual cost varies significantly by drafter/architect and project complexity."
  }),
  sampleItemV2({
    id: "addition_engineering",
    projectType: "addition",
    category: "permit_design_allowance",
    subcategory: "engineering",
    unit: "allowance",
    percentageOfCategory: null,
    costs: { low: 2200, typical: 5000, high: 10000, minimum: 1200, maximum: 18000 },
    components: { labourPercent: 10, materialPercent: 90 },
    multipliers: { complexity: 1.0, finishLevel: {}, regionalOverride: null },
    adminNotes: "A stamped structural engineering package (e.g. footings, beam sizing, structural tie-in) - separate from architectural/design drawings above. Scope and cost depend heavily on structural complexity and cannot be finalized without an engineer's review."
  }),
  sampleItemV2({
    id: "addition_permit_allowance",
    projectType: "addition",
    category: "permit_design_allowance",
    subcategory: "permit_fees",
    unit: "allowance",
    percentageOfCategory: null,
    costs: { low: 1200, typical: 2800, high: 5500, minimum: 700, maximum: 9500 },
    components: { labourPercent: 5, materialPercent: 95 },
    multipliers: { complexity: 1.0, finishLevel: {}, regionalOverride: null },
    adminNotes: "Municipal building permit fees for a physical addition - required in essentially every Canadian jurisdiction; actual fees vary by municipality and project value."
  }),
  sampleItemV2({
    id: "addition_demolition",
    projectType: "addition",
    category: "demolition",
    subcategory: null,
    unit: "percentage",
    percentageOfCategory: "base_construction",
    costs: { low: 4, typical: 7, high: 12, minimum: 2, maximum: 18 },
    components: { labourPercent: 90, materialPercent: 10 },
    multipliers: { complexity: 1.0, finishLevel: {}, regionalOverride: null },
    adminNotes: "Removing existing exterior wall/siding/roofing to open up the tie-in point for the addition."
  })
];

// --- Shared helper for a single pricing line item, used by the Phase 11 (deck/patio) and Phase 13A (interior room) sections below ---
function li(
  id: string,
  projectType: ProjectType,
  category: string,
  subcategory: string | null,
  unit: PricingUnit,
  costs: PricingCostRange,
  opts: {
    components?: PricingCostComponents;
    multipliers?: PricingMultipliers;
    percentageOfCategory?: string | null;
    adminNotes?: string;
  } = {}
): PricingLineItem {
  return sampleItemV2({
    id,
    projectType,
    category,
    subcategory,
    unit,
    percentageOfCategory: opts.percentageOfCategory ?? null,
    costs,
    components: opts.components ?? { labourPercent: 50, materialPercent: 50 },
    multipliers: opts.multipliers ?? { complexity: 1.0, finishLevel: {}, regionalOverride: null },
    adminNotes: opts.adminNotes
  });
}

// --- Phase 11: Deck & Patio -----------------------------------------------------------------------------------------------
const DECK_PATIO_FINISH_MULTIPLIERS_V2 = { budget: 0.7, standard: 1.0, premium: 1.5, luxury: 2.3 };

export const phase11DeckPatioSampleItems: PricingLineItem[] = [
  li("deck_patio_deck_base", "deck_patio", "base_construction", "deck", "sqft", { low: 45, typical: 65, high: 95, minimum: 30, maximum: 140 }, {
    components: { labourPercent: 60, materialPercent: 40 },
    multipliers: { complexity: 1.0, finishLevel: DECK_PATIO_FINISH_MULTIPLIERS_V2, regionalOverride: null },
    adminNotes: "Deck substructure - footings, posts, beams, joists - before surface material (below) or any add-ons are priced."
  }),
  li("deck_patio_patio_base", "deck_patio", "base_construction", "patio", "sqft", { low: 16, typical: 28, high: 42, minimum: 10, maximum: 65 }, {
    components: { labourPercent: 55, materialPercent: 45 },
    multipliers: { complexity: 1.0, finishLevel: DECK_PATIO_FINISH_MULTIPLIERS_V2, regionalOverride: null },
    adminNotes: "Patio base preparation - excavation, sub-base, and grading - before the surface material (below) is priced."
  }),
  li("deck_material_pressure_treated", "deck_patio", "surface_material", "pressure_treated", "sqft", { low: 8, typical: 12, high: 18, minimum: 5, maximum: 26 }, {
    components: { labourPercent: 40, materialPercent: 60 },
    multipliers: { complexity: 1.0, finishLevel: DECK_PATIO_FINISH_MULTIPLIERS_V2, regionalOverride: null }
  }),
  li("deck_material_cedar", "deck_patio", "surface_material", "cedar", "sqft", { low: 14, typical: 20, high: 30, minimum: 9, maximum: 42 }, {
    components: { labourPercent: 38, materialPercent: 62 },
    multipliers: { complexity: 1.0, finishLevel: DECK_PATIO_FINISH_MULTIPLIERS_V2, regionalOverride: null }
  }),
  li("deck_material_composite", "deck_patio", "surface_material", "composite", "sqft", { low: 20, typical: 28, high: 42, minimum: 14, maximum: 58 }, {
    components: { labourPercent: 35, materialPercent: 65 },
    multipliers: { complexity: 1.0, finishLevel: DECK_PATIO_FINISH_MULTIPLIERS_V2, regionalOverride: null }
  }),
  li("deck_material_premium", "deck_patio", "surface_material", "premium_custom", "sqft", { low: 32, typical: 45, high: 70, minimum: 22, maximum: 100 }, {
    components: { labourPercent: 32, materialPercent: 68 },
    multipliers: { complexity: 1.0, finishLevel: DECK_PATIO_FINISH_MULTIPLIERS_V2, regionalOverride: null }
  }),
  li("patio_material_concrete", "deck_patio", "surface_material", "concrete", "sqft", { low: 6, typical: 10, high: 16, minimum: 4, maximum: 24 }, {
    components: { labourPercent: 45, materialPercent: 55 },
    multipliers: { complexity: 1.0, finishLevel: DECK_PATIO_FINISH_MULTIPLIERS_V2, regionalOverride: null }
  }),
  li("patio_material_pavers", "deck_patio", "surface_material", "pavers", "sqft", { low: 14, typical: 20, high: 30, minimum: 9, maximum: 42 }, {
    components: { labourPercent: 45, materialPercent: 55 },
    multipliers: { complexity: 1.0, finishLevel: DECK_PATIO_FINISH_MULTIPLIERS_V2, regionalOverride: null }
  }),
  li("patio_material_natural_stone", "deck_patio", "surface_material", "natural_stone", "sqft", { low: 22, typical: 32, high: 50, minimum: 15, maximum: 72 }, {
    components: { labourPercent: 42, materialPercent: 58 },
    multipliers: { complexity: 1.0, finishLevel: DECK_PATIO_FINISH_MULTIPLIERS_V2, regionalOverride: null }
  }),
  li("patio_material_premium", "deck_patio", "surface_material", "premium_custom", "sqft", { low: 30, typical: 42, high: 65, minimum: 20, maximum: 95 }, {
    components: { labourPercent: 40, materialPercent: 60 },
    multipliers: { complexity: 1.0, finishLevel: DECK_PATIO_FINISH_MULTIPLIERS_V2, regionalOverride: null }
  }),
  li("deck_elevated_framing", "deck_patio", "structural", "elevated_framing", "sqft", { low: 10, typical: 16, high: 26, minimum: 6, maximum: 38 }, {
    components: { labourPercent: 65, materialPercent: 35 },
    multipliers: { complexity: 1.1, finishLevel: DECK_PATIO_FINISH_MULTIPLIERS_V2, regionalOverride: null },
    adminNotes: "Additional structural framing/footings required to support an elevated (above ground-level) deck, beyond the base substructure."
  }),
  li("deck_patio_roof_pergola", "deck_patio", "roof_cover", "pergola", "sqft", { low: 18, typical: 28, high: 45, minimum: 12, maximum: 65 }, {
    components: { labourPercent: 45, materialPercent: 55 },
    multipliers: { complexity: 1.0, finishLevel: DECK_PATIO_FINISH_MULTIPLIERS_V2, regionalOverride: null }
  }),
  li("deck_patio_roof_solid", "deck_patio", "roof_cover", "solid_roof", "sqft", { low: 35, typical: 55, high: 85, minimum: 22, maximum: 120 }, {
    components: { labourPercent: 42, materialPercent: 58 },
    multipliers: { complexity: 1.05, finishLevel: DECK_PATIO_FINISH_MULTIPLIERS_V2, regionalOverride: null }
  }),
  li("deck_patio_stairs", "deck_patio", "stairs", null, "item", { low: 800, typical: 1500, high: 2800, minimum: 500, maximum: 4200 }, {
    components: { labourPercent: 60, materialPercent: 40 },
    multipliers: { complexity: 1.0, finishLevel: DECK_PATIO_FINISH_MULTIPLIERS_V2, regionalOverride: null },
    adminNotes: "Priced per set of stairs (quantity reflects the number of stair sets)."
  }),
  li("deck_patio_railing_standard", "deck_patio", "railings", "standard", "linear_ft", { low: 45, typical: 65, high: 95, minimum: 30, maximum: 135 }, {
    components: { labourPercent: 50, materialPercent: 50 },
    multipliers: { complexity: 1.0, finishLevel: DECK_PATIO_FINISH_MULTIPLIERS_V2, regionalOverride: null }
  }),
  li("deck_patio_railing_premium", "deck_patio", "railings", "premium_glass_cable", "linear_ft", { low: 90, typical: 140, high: 220, minimum: 60, maximum: 320 }, {
    components: { labourPercent: 40, materialPercent: 60 },
    multipliers: { complexity: 1.0, finishLevel: DECK_PATIO_FINISH_MULTIPLIERS_V2, regionalOverride: null }
  }),
  li("deck_patio_additional_level", "deck_patio", "additional_level", null, "item", { low: 3500, typical: 6000, high: 11000, minimum: 2200, maximum: 16000 }, {
    components: { labourPercent: 60, materialPercent: 40 },
    multipliers: { complexity: 1.05, finishLevel: DECK_PATIO_FINISH_MULTIPLIERS_V2, regionalOverride: null },
    adminNotes: "Priced per additional level beyond the first (quantity 1 for two levels, 2 for three-plus levels)."
  }),
  li("deck_patio_demolition", "deck_patio", "demolition", null, "percentage", { low: 4, typical: 7, high: 12, minimum: 2, maximum: 18 }, {
    components: { labourPercent: 90, materialPercent: 10 },
    percentageOfCategory: "base_construction",
    adminNotes: "Removing an existing deck/patio structure before rebuilding, priced as a percentage of the new base construction cost."
  }),
  li("deck_patio_accessibility", "deck_patio", "accessibility", null, "allowance", { low: 1500, typical: 3200, high: 6500, minimum: 800, maximum: 11000 }, {
    components: { labourPercent: 65, materialPercent: 35 },
    multipliers: { complexity: 1.1, finishLevel: {}, regionalOverride: null }
  }),
  li("deck_patio_permit_design", "deck_patio", "permit_design", null, "allowance", { low: 600, typical: 1500, high: 3200, minimum: 300, maximum: 6000 }, {
    components: { labourPercent: 15, materialPercent: 5 }
  })
];

// --- Phase 13A: Interior Room Estimators (living room, family room, dining room, bedroom, primary bedroom, home office, laundry room, mudroom, entryway/foyer, hallway) ---
const INTERIOR_ROOM_FINISH_MULTIPLIERS_V2 = { budget: 0.72, standard: 1.0, premium: 1.5, luxury: 2.2 };

const INTERIOR_ROOM_FLOORING_COSTS: Record<"basic" | "mid_range" | "premium", PricingCostRange> = {
  basic: { low: 5, typical: 7, high: 11, minimum: 3, maximum: 16 },
  mid_range: { low: 9, typical: 13, high: 19, minimum: 6, maximum: 26 },
  premium: { low: 15, typical: 22, high: 32, minimum: 10, maximum: 42 }
};
const INTERIOR_ROOM_PAINTING_COSTS: Record<"walls_only" | "full_room", PricingCostRange> = {
  walls_only: { low: 1.5, typical: 2.5, high: 4, minimum: 1, maximum: 6 },
  full_room: { low: 2.5, typical: 4, high: 6.5, minimum: 1.5, maximum: 9 }
};
const INTERIOR_ROOM_TRIM_COSTS: Record<"standard_upgrade" | "premium_millwork", PricingCostRange> = {
  standard_upgrade: { low: 500, typical: 900, high: 1500, minimum: 300, maximum: 2200 },
  premium_millwork: { low: 1500, typical: 2600, high: 4200, minimum: 1000, maximum: 6000 }
};
const INTERIOR_ROOM_LIGHTING_COSTS: Record<"standard_swap" | "statement_lighting", PricingCostRange> = {
  standard_swap: { low: 250, typical: 450, high: 750, minimum: 150, maximum: 1100 },
  statement_lighting: { low: 900, typical: 1800, high: 3200, minimum: 600, maximum: 5000 }
};
const INTERIOR_ROOM_BUILT_INS_COSTS: Record<"standard" | "custom", PricingCostRange> = {
  standard: { low: 900, typical: 1600, high: 2800, minimum: 600, maximum: 4000 },
  custom: { low: 2800, typical: 4800, high: 8000, minimum: 1800, maximum: 12000 }
};
const INTERIOR_ROOM_WINDOWS_DOORS_COSTS: Record<"replace_existing" | "add_new", PricingCostRange> = {
  replace_existing: { low: 700, typical: 1300, high: 2200, minimum: 400, maximum: 3200 },
  add_new: { low: 1800, typical: 3200, high: 5200, minimum: 1200, maximum: 7500 }
};
const INTERIOR_ROOM_ELECTRICAL_COSTS: PricingCostRange = { low: 350, typical: 650, high: 1100, minimum: 200, maximum: 1600 };
const INTERIOR_ROOM_DEMOLITION_COSTS: PricingCostRange = { low: 4, typical: 7, high: 11, minimum: 2, maximum: 16 };
const INTERIOR_ROOM_ACCESSIBILITY_COSTS: PricingCostRange = { low: 1200, typical: 2800, high: 5500, minimum: 600, maximum: 9000 };
const INTERIOR_ROOM_PERMIT_DESIGN_COSTS: PricingCostRange = { low: 300, typical: 750, high: 1600, minimum: 150, maximum: 2800 };

/**
 * Builds the shared category set (base renovation, flooring, painting,
 * lighting, demolition, accessibility, permit/design, plus the optional
 * trim/built-ins/windows-doors/generic-electrical categories) for one
 * Phase 13A interior room project type. Each of the ten project types
 * calls this with its own id prefix, base renovation cost, and which
 * optional categories it actually has (see rules/<room>.ts's doc
 * comments for the "why" behind each inclusion/exclusion), then appends
 * its own unique category(s) - e.g. living room's fireplace, home
 * office's tiered electrical/data - directly in the section below.
 */
function buildInteriorRoomCoreItems(
  projectType: ProjectType,
  prefix: string,
  baseRenovationCosts: PricingCostRange,
  opts: { hasTrim?: boolean; hasBuiltIns?: boolean; hasWindowsDoors?: boolean; hasGenericElectrical?: boolean } = {}
): PricingLineItem[] {
  const { hasTrim = true, hasBuiltIns = true, hasWindowsDoors = true, hasGenericElectrical = true } = opts;
  const items: PricingLineItem[] = [
    li(`${prefix}_base_renovation`, projectType, "base_renovation", null, "sqft", baseRenovationCosts, {
      components: { labourPercent: 55, materialPercent: 45 },
      multipliers: { complexity: 1.0, finishLevel: INTERIOR_ROOM_FINISH_MULTIPLIERS_V2, regionalOverride: null }
    }),
    li(`${prefix}_flooring_basic`, projectType, "flooring", "basic", "sqft", INTERIOR_ROOM_FLOORING_COSTS.basic, {
      components: { labourPercent: 45, materialPercent: 55 }
    }),
    li(`${prefix}_flooring_mid`, projectType, "flooring", "mid_range", "sqft", INTERIOR_ROOM_FLOORING_COSTS.mid_range, {
      components: { labourPercent: 45, materialPercent: 55 }
    }),
    li(`${prefix}_flooring_premium`, projectType, "flooring", "premium", "sqft", INTERIOR_ROOM_FLOORING_COSTS.premium, {
      components: { labourPercent: 40, materialPercent: 60 }
    }),
    li(`${prefix}_painting_walls`, projectType, "painting", "walls_only", "sqft", INTERIOR_ROOM_PAINTING_COSTS.walls_only, {
      components: { labourPercent: 70, materialPercent: 30 }
    }),
    li(`${prefix}_painting_full`, projectType, "painting", "full_room", "sqft", INTERIOR_ROOM_PAINTING_COSTS.full_room, {
      components: { labourPercent: 65, materialPercent: 35 }
    }),
    li(`${prefix}_lighting_standard`, projectType, "lighting", "standard_swap", "item", INTERIOR_ROOM_LIGHTING_COSTS.standard_swap, {
      components: { labourPercent: 50, materialPercent: 50 }
    }),
    li(`${prefix}_lighting_statement`, projectType, "lighting", "statement_lighting", "item", INTERIOR_ROOM_LIGHTING_COSTS.statement_lighting, {
      components: { labourPercent: 40, materialPercent: 60 }
    }),
    li(`${prefix}_demolition`, projectType, "demolition", null, "percentage", INTERIOR_ROOM_DEMOLITION_COSTS, {
      components: { labourPercent: 90, materialPercent: 10 },
      percentageOfCategory: "base_renovation"
    }),
    li(`${prefix}_accessibility`, projectType, "accessibility", null, "allowance", INTERIOR_ROOM_ACCESSIBILITY_COSTS, {
      components: { labourPercent: 65, materialPercent: 35 }
    }),
    li(`${prefix}_permit_design`, projectType, "permit_design", null, "allowance", INTERIOR_ROOM_PERMIT_DESIGN_COSTS, {
      components: { labourPercent: 20, materialPercent: 10 }
    })
  ];
  if (hasTrim) {
    items.push(
      li(`${prefix}_trim_standard`, projectType, "trim", "standard_upgrade", "item", INTERIOR_ROOM_TRIM_COSTS.standard_upgrade, {
        components: { labourPercent: 60, materialPercent: 40 }
      }),
      li(`${prefix}_trim_premium`, projectType, "trim", "premium_millwork", "item", INTERIOR_ROOM_TRIM_COSTS.premium_millwork, {
        components: { labourPercent: 55, materialPercent: 45 }
      })
    );
  }
  if (hasBuiltIns) {
    items.push(
      li(`${prefix}_built_ins_standard`, projectType, "built_ins", "standard", "item", INTERIOR_ROOM_BUILT_INS_COSTS.standard, {
        components: { labourPercent: 45, materialPercent: 55 }
      }),
      li(`${prefix}_built_ins_custom`, projectType, "built_ins", "custom", "item", INTERIOR_ROOM_BUILT_INS_COSTS.custom, {
        components: { labourPercent: 40, materialPercent: 60 }
      })
    );
  }
  if (hasWindowsDoors) {
    items.push(
      li(`${prefix}_windows_doors_replace`, projectType, "windows_doors", "replace_existing", "item", INTERIOR_ROOM_WINDOWS_DOORS_COSTS.replace_existing, {
        components: { labourPercent: 35, materialPercent: 65 }
      }),
      li(`${prefix}_windows_doors_add`, projectType, "windows_doors", "add_new", "item", INTERIOR_ROOM_WINDOWS_DOORS_COSTS.add_new, {
        components: { labourPercent: 35, materialPercent: 65 }
      })
    );
  }
  if (hasGenericElectrical) {
    items.push(
      li(`${prefix}_electrical`, projectType, "electrical", null, "item", INTERIOR_ROOM_ELECTRICAL_COSTS, {
        components: { labourPercent: 70, materialPercent: 30 }
      })
    );
  }
  return items;
}

export const phase13aInteriorRoomSampleItems: PricingLineItem[] = [
  // Living Room - full shared category set + fireplace
  ...buildInteriorRoomCoreItems("living_room", "living_room", { low: 40, typical: 55, high: 80, minimum: 25, maximum: 120 }),
  li("living_room_fireplace", "living_room", "fireplace", null, "item", { low: 3000, typical: 5500, high: 9500, minimum: 2000, maximum: 14000 }, {
    components: { labourPercent: 60, materialPercent: 40 }
  }),

  // Family Room - full shared category set + wet bar
  ...buildInteriorRoomCoreItems("family_room", "family_room", { low: 38, typical: 52, high: 75, minimum: 24, maximum: 110 }),
  li("family_room_wet_bar", "family_room", "wet_bar", null, "item", { low: 4500, typical: 8000, high: 14000, minimum: 3000, maximum: 20000 }, {
    components: { labourPercent: 45, materialPercent: 55 }
  }),

  // Dining Room - no windows/doors category + accent wall treatment
  ...buildInteriorRoomCoreItems("dining_room", "dining_room", { low: 36, typical: 50, high: 72, minimum: 22, maximum: 105 }, { hasWindowsDoors: false }),
  li("dining_room_wall_treatment", "dining_room", "wall_treatment", null, "item", { low: 1200, typical: 2400, high: 4200, minimum: 800, maximum: 6500 }, {
    components: { labourPercent: 55, materialPercent: 45 }
  }),

  // Bedroom - full shared category set + ensuite rough-in
  ...buildInteriorRoomCoreItems("bedroom", "bedroom", { low: 32, typical: 45, high: 65, minimum: 20, maximum: 95 }),
  li("bedroom_ensuite_rough_in", "bedroom", "plumbing", "ensuite_rough_in", "item", { low: 1800, typical: 3200, high: 5500, minimum: 1200, maximum: 8000 }, {
    components: { labourPercent: 75, materialPercent: 25 }
  }),

  // Primary Bedroom - full shared category set + closet system tiers, sitting area, ensuite rough-in
  ...buildInteriorRoomCoreItems("primary_bedroom", "primary_bedroom", { low: 42, typical: 60, high: 88, minimum: 26, maximum: 130 }),
  li("primary_bedroom_closet_reach_in_upgrade", "primary_bedroom", "closet_system", "reach_in_upgrade", "item", { low: 1000, typical: 1900, high: 3200, minimum: 700, maximum: 4500 }, {
    components: { labourPercent: 50, materialPercent: 50 }
  }),
  li("primary_bedroom_closet_walk_in_system", "primary_bedroom", "closet_system", "walk_in_system", "item", { low: 3800, typical: 7000, high: 12500, minimum: 2500, maximum: 18000 }, {
    components: { labourPercent: 40, materialPercent: 60 }
  }),
  li("primary_bedroom_sitting_area", "primary_bedroom", "sitting_area", null, "item", { low: 2500, typical: 4800, high: 8500, minimum: 1500, maximum: 13000 }, {
    components: { labourPercent: 55, materialPercent: 45 }
  }),
  li("primary_bedroom_ensuite_rough_in", "primary_bedroom", "plumbing", "ensuite_rough_in", "item", { low: 2200, typical: 3800, high: 6500, minimum: 1500, maximum: 9500 }, {
    components: { labourPercent: 75, materialPercent: 25 }
  }),

  // Home Office - no windows/doors category, no generic electrical (its own tiered electrical/data instead)
  ...buildInteriorRoomCoreItems("home_office", "home_office", { low: 34, typical: 48, high: 70, minimum: 22, maximum: 100 }, {
    hasWindowsDoors: false,
    hasGenericElectrical: false
  }),
  li("home_office_electrical_extra_circuits", "home_office", "electrical", "extra_circuits", "item", { low: 700, typical: 1300, high: 2200, minimum: 400, maximum: 3200 }, {
    components: { labourPercent: 80, materialPercent: 20 }
  }),
  li("home_office_electrical_network_av", "home_office", "electrical", "network_av", "item", { low: 1600, typical: 2900, high: 5000, minimum: 1000, maximum: 7500 }, {
    components: { labourPercent: 65, materialPercent: 35 }
  }),

  // Laundry Room - no trim or windows/doors category, dedicated plumbing scope tier
  ...buildInteriorRoomCoreItems("laundry_room", "laundry_room", { low: 45, typical: 70, high: 105, minimum: 28, maximum: 150 }, {
    hasTrim: false,
    hasWindowsDoors: false
  }),
  li("laundry_room_plumbing_relocate_hookups", "laundry_room", "plumbing", "relocate_hookups", "item", { low: 1400, typical: 2600, high: 4500, minimum: 900, maximum: 6500 }, {
    components: { labourPercent: 80, materialPercent: 20 }
  }),
  li("laundry_room_plumbing_add_utility_sink", "laundry_room", "plumbing", "add_utility_sink", "item", { low: 2000, typical: 3600, high: 6200, minimum: 1400, maximum: 9000 }, {
    components: { labourPercent: 70, materialPercent: 30 }
  }),

  // Mudroom - no trim category, heated flooring (scales with room size)
  ...buildInteriorRoomCoreItems("mudroom", "mudroom", { low: 42, typical: 65, high: 98, minimum: 26, maximum: 140 }, { hasTrim: false }),
  li("mudroom_heated_floor", "mudroom", "heated_floor", null, "sqft", { low: 10, typical: 16, high: 26, minimum: 6, maximum: 38 }, {
    components: { labourPercent: 40, materialPercent: 60 }
  }),

  // Entryway / Foyer - full shared category set
  ...buildInteriorRoomCoreItems("entryway_foyer", "entryway_foyer", { low: 38, typical: 58, high: 85, minimum: 24, maximum: 120 }),

  // Hallway - smallest scope: flooring, painting, trim, lighting only (no built-ins, no windows/doors)
  ...buildInteriorRoomCoreItems("hallway", "hallway", { low: 18, typical: 30, high: 48, minimum: 10, maximum: 70 }, {
    hasBuiltIns: false,
    hasWindowsDoors: false
  })
];

// --- Phase 13B: Garage, Attic, Exterior ---------------------------------------------------------------------------------
const GARAGE_FINISH_MULTIPLIERS_V2 = { budget: 0.75, standard: 1.0, premium: 1.45, luxury: 2.05 };
const ATTIC_FINISH_MULTIPLIERS_V2 = { budget: 0.75, standard: 1.0, premium: 1.5, luxury: 2.15 };
const EXTERIOR_FINISH_MULTIPLIERS_V2 = { budget: 0.75, standard: 1.0, premium: 1.45, luxury: 2.0 };

export const phase13bGarageSampleItems: PricingLineItem[] = [
  li("garage_base_renovation", "garage", "base_renovation", null, "sqft", { low: 18, typical: 28, high: 42, minimum: 12, maximum: 60 }, {
    components: { labourPercent: 55, materialPercent: 45 },
    multipliers: { complexity: 1.0, finishLevel: GARAGE_FINISH_MULTIPLIERS_V2, regionalOverride: null }
  }),
  li("garage_flooring_basic_coating", "garage", "flooring", "basic_coating", "sqft", { low: 1.5, typical: 2.5, high: 4, minimum: 1, maximum: 6 }, {
    components: { labourPercent: 40, materialPercent: 60 }
  }),
  li("garage_flooring_epoxy", "garage", "flooring", "epoxy", "sqft", { low: 4, typical: 6.5, high: 10, minimum: 2.5, maximum: 15 }, {
    components: { labourPercent: 35, materialPercent: 65 }
  }),
  li("garage_insulation_walls", "garage", "insulation", "walls_only", "sqft", { low: 2.5, typical: 4, high: 6.5, minimum: 1.5, maximum: 9 }, {
    components: { labourPercent: 50, materialPercent: 50 }
  }),
  li("garage_insulation_walls_ceiling", "garage", "insulation", "walls_and_ceiling", "sqft", { low: 4, typical: 6.5, high: 10, minimum: 2.5, maximum: 14 }, {
    components: { labourPercent: 48, materialPercent: 52 }
  }),
  li("garage_drywall_basic", "garage", "drywall", "basic", "sqft", { low: 3, typical: 5, high: 8, minimum: 2, maximum: 11 }, {
    components: { labourPercent: 60, materialPercent: 40 }
  }),
  li("garage_drywall_finished_painted", "garage", "drywall", "finished_painted", "sqft", { low: 5, typical: 8, high: 12.5, minimum: 3, maximum: 17 }, {
    components: { labourPercent: 58, materialPercent: 42 }
  }),
  li("garage_door_single_standard", "garage", "garage_door", "single_standard", "item", { low: 900, typical: 1500, high: 2400, minimum: 600, maximum: 3200 }, {
    components: { labourPercent: 30, materialPercent: 70 },
    multipliers: { complexity: 1.0, finishLevel: GARAGE_FINISH_MULTIPLIERS_V2, regionalOverride: null }
  }),
  li("garage_door_single_insulated", "garage", "garage_door", "single_insulated", "item", { low: 1500, typical: 2400, high: 3800, minimum: 1000, maximum: 5200 }, {
    components: { labourPercent: 28, materialPercent: 72 },
    multipliers: { complexity: 1.0, finishLevel: GARAGE_FINISH_MULTIPLIERS_V2, regionalOverride: null }
  }),
  li("garage_door_double_standard", "garage", "garage_door", "double_standard", "item", { low: 1600, typical: 2600, high: 4200, minimum: 1100, maximum: 5800 }, {
    components: { labourPercent: 28, materialPercent: 72 },
    multipliers: { complexity: 1.0, finishLevel: GARAGE_FINISH_MULTIPLIERS_V2, regionalOverride: null }
  }),
  li("garage_door_double_insulated", "garage", "garage_door", "double_insulated", "item", { low: 2600, typical: 4200, high: 6800, minimum: 1800, maximum: 9500 }, {
    components: { labourPercent: 26, materialPercent: 74 },
    multipliers: { complexity: 1.0, finishLevel: GARAGE_FINISH_MULTIPLIERS_V2, regionalOverride: null }
  }),
  li("garage_electrical_subpanel_240v", "garage", "electrical", "subpanel_240v", "item", { low: 1200, typical: 2200, high: 3800, minimum: 800, maximum: 5500 }, {
    components: { labourPercent: 70, materialPercent: 30 }
  }),
  li("garage_electrical_workshop_circuits", "garage", "electrical", "workshop_circuits", "item", { low: 1800, typical: 3200, high: 5500, minimum: 1200, maximum: 8000 }, {
    components: { labourPercent: 70, materialPercent: 30 }
  }),
  li("garage_storage_basic_shelving", "garage", "storage", "basic_shelving", "item", { low: 500, typical: 900, high: 1500, minimum: 300, maximum: 2200 }, {
    components: { labourPercent: 35, materialPercent: 65 }
  }),
  li("garage_storage_custom_system", "garage", "storage", "custom_system", "item", { low: 2200, typical: 4000, high: 7000, minimum: 1500, maximum: 10000 }, {
    components: { labourPercent: 30, materialPercent: 70 }
  }),
  li("garage_heating_electric_baseboard", "garage", "heating", "electric_baseboard", "item", { low: 900, typical: 1600, high: 2800, minimum: 600, maximum: 4000 }, {
    components: { labourPercent: 55, materialPercent: 45 }
  }),
  li("garage_heating_radiant_floor", "garage", "heating", "radiant_floor", "item", { low: 3500, typical: 6000, high: 10000, minimum: 2500, maximum: 15000 }, {
    components: { labourPercent: 45, materialPercent: 55 }
  }),
  li("garage_heating_gas_forced_air", "garage", "heating", "gas_forced_air", "item", { low: 4500, typical: 8000, high: 13500, minimum: 3200, maximum: 19000 }, {
    components: { labourPercent: 45, materialPercent: 55 },
    adminNotes: "Includes unit and standard gas/venting hookup - assumes a gas line is reasonably close; a long new gas run is not included."
  }),
  li("garage_plumbing", "garage", "plumbing", null, "allowance", { low: 1200, typical: 2400, high: 4500, minimum: 800, maximum: 7000 }, {
    components: { labourPercent: 75, materialPercent: 25 },
    adminNotes: "Covers a modest addition such as a floor drain or hose bib - a full bathroom/laundry rough-in in a garage is out of scope for this allowance."
  }),
  li("garage_demolition", "garage", "demolition", null, "percentage", { low: 4, typical: 7, high: 12, minimum: 2, maximum: 18 }, {
    components: { labourPercent: 90, materialPercent: 10 },
    percentageOfCategory: "base_renovation"
  }),
  li("garage_accessibility", "garage", "accessibility", null, "allowance", { low: 1200, typical: 2800, high: 5500, minimum: 600, maximum: 9000 }, {
    components: { labourPercent: 65, materialPercent: 35 }
  }),
  li("garage_permit_design", "garage", "permit_design", null, "allowance", { low: 400, typical: 1000, high: 2200, minimum: 200, maximum: 3800 }, {
    components: { labourPercent: 20, materialPercent: 10 }
  })
];

export const phase13bAtticSampleItems: PricingLineItem[] = [
  li("attic_insulation_standard", "attic", "insulation", "standard_upgrade", "sqft", { low: 2, typical: 3.2, high: 5, minimum: 1.2, maximum: 7 }, {
    components: { labourPercent: 45, materialPercent: 55 }
  }),
  li("attic_insulation_high_r", "attic", "insulation", "high_r_upgrade", "sqft", { low: 3.2, typical: 5, high: 7.5, minimum: 2, maximum: 10.5 }, {
    components: { labourPercent: 42, materialPercent: 58 }
  }),
  li("attic_insulation_spray_foam", "attic", "insulation", "spray_foam_premium", "sqft", { low: 5.5, typical: 8.5, high: 13, minimum: 3.5, maximum: 18 }, {
    components: { labourPercent: 38, materialPercent: 62 }
  }),
  li("attic_finishing_base", "attic", "base_renovation", null, "sqft", { low: 35, typical: 50, high: 75, minimum: 22, maximum: 105 }, {
    components: { labourPercent: 55, materialPercent: 45 },
    multipliers: { complexity: 1.0, finishLevel: ATTIC_FINISH_MULTIPLIERS_V2, regionalOverride: null },
    adminNotes: "Framing/collar-tie work, subfloor, drywall, and general finishing to bring an attic up to livable-space shell condition, before flooring (below) or insulation is added."
  }),
  li("attic_flooring_basic", "attic", "flooring", "basic", "sqft", { low: 5, typical: 7, high: 11, minimum: 3, maximum: 16 }, {
    components: { labourPercent: 45, materialPercent: 55 }
  }),
  li("attic_flooring_mid", "attic", "flooring", "mid_range", "sqft", { low: 9, typical: 13, high: 19, minimum: 6, maximum: 26 }, {
    components: { labourPercent: 45, materialPercent: 55 }
  }),
  li("attic_flooring_premium", "attic", "flooring", "premium", "sqft", { low: 15, typical: 22, high: 32, minimum: 10, maximum: 42 }, {
    components: { labourPercent: 40, materialPercent: 60 }
  }),
  li("attic_electrical_basic_lighting", "attic", "electrical", "basic_lighting", "item", { low: 350, typical: 650, high: 1100, minimum: 200, maximum: 1600 }, {
    components: { labourPercent: 75, materialPercent: 25 }
  }),
  li("attic_electrical_full_wiring", "attic", "electrical", "full_wiring", "item", { low: 1200, typical: 2200, high: 3800, minimum: 800, maximum: 5500 }, {
    components: { labourPercent: 70, materialPercent: 30 }
  }),
  li("attic_access_pull_down_stairs", "attic", "access", "pull_down_stairs", "item", { low: 500, typical: 900, high: 1500, minimum: 300, maximum: 2200 }, {
    components: { labourPercent: 55, materialPercent: 45 }
  }),
  li("attic_access_permanent_staircase", "attic", "access", "permanent_staircase", "item", { low: 3500, typical: 6500, high: 11000, minimum: 2200, maximum: 15000 }, {
    components: { labourPercent: 60, materialPercent: 40 },
    adminNotes: "A built, code-compliant staircase, including the floor opening/structural framing to support it - typically required for finished attic living space."
  }),
  li("attic_access_scuttle_hole", "attic", "access", "scuttle_hole", "item", { low: 200, typical: 400, high: 700, minimum: 100, maximum: 1000 }, {
    components: { labourPercent: 60, materialPercent: 40 }
  }),
  li("attic_demolition", "attic", "demolition", null, "percentage", { low: 4, typical: 7, high: 12, minimum: 2, maximum: 18 }, {
    components: { labourPercent: 90, materialPercent: 10 },
    percentageOfCategory: "base_renovation"
  }),
  li("attic_accessibility", "attic", "accessibility", null, "allowance", { low: 1000, typical: 2200, high: 4500, minimum: 500, maximum: 7500 }, {
    components: { labourPercent: 65, materialPercent: 35 }
  }),
  li("attic_permit_design", "attic", "permit_design", null, "allowance", { low: 400, typical: 1000, high: 2200, minimum: 200, maximum: 3800 }, {
    components: { labourPercent: 20, materialPercent: 10 }
  })
];

export const phase13bExteriorSampleItems: PricingLineItem[] = [
  li("exterior_siding_partial_repair", "exterior", "siding", "partial_repair", "sqft", { low: 8, typical: 14, high: 22, minimum: 5, maximum: 32 }, {
    components: { labourPercent: 55, materialPercent: 45 },
    multipliers: { complexity: 1.0, finishLevel: EXTERIOR_FINISH_MULTIPLIERS_V2, regionalOverride: null }
  }),
  li("exterior_siding_full_replacement", "exterior", "siding", "full_replacement", "sqft", { low: 14, typical: 22, high: 34, minimum: 9, maximum: 48 }, {
    components: { labourPercent: 45, materialPercent: 55 },
    multipliers: { complexity: 1.0, finishLevel: EXTERIOR_FINISH_MULTIPLIERS_V2, regionalOverride: null }
  }),
  li("exterior_windows_some_replaced", "exterior", "windows", "some_replaced", "item", { low: 3500, typical: 6500, high: 11000, minimum: 2000, maximum: 16000 }, {
    components: { labourPercent: 35, materialPercent: 65 },
    multipliers: { complexity: 1.0, finishLevel: EXTERIOR_FINISH_MULTIPLIERS_V2, regionalOverride: null }
  }),
  li("exterior_windows_full_replacement", "exterior", "windows", "full_replacement", "item", { low: 9000, typical: 16000, high: 27000, minimum: 6000, maximum: 38000 }, {
    components: { labourPercent: 32, materialPercent: 68 },
    multipliers: { complexity: 1.0, finishLevel: EXTERIOR_FINISH_MULTIPLIERS_V2, regionalOverride: null }
  }),
  li("exterior_entry_door", "exterior", "doors", null, "item", { low: 1200, typical: 2200, high: 3800, minimum: 800, maximum: 5500 }, {
    components: { labourPercent: 35, materialPercent: 65 },
    multipliers: { complexity: 1.0, finishLevel: EXTERIOR_FINISH_MULTIPLIERS_V2, regionalOverride: null },
    adminNotes: "Priced per exterior entry door (quantity reflects the number of doors)."
  }),
  li("exterior_roofing_minor_repair", "exterior", "roofing", "minor_repair_flashing", "allowance", { low: 800, typical: 1800, high: 3500, minimum: 500, maximum: 5500 }, {
    components: { labourPercent: 80, materialPercent: 20 },
    adminNotes: "Minor repair or flashing/edge work only - NOT a full roof replacement. See the standing warning attached whenever this category is priced."
  }),
  li("exterior_roofing_tied_to_siding", "exterior", "roofing", "tied_to_siding_work", "allowance", { low: 1500, typical: 3000, high: 5500, minimum: 900, maximum: 8000 }, {
    components: { labourPercent: 75, materialPercent: 25 },
    adminNotes: "Roofing edge/flashing work coordinated with a siding job - still not a full roof replacement."
  }),
  li("exterior_trim_fascia_soffit_basic", "exterior", "trim", "basic", "sqft", { low: 4, typical: 7, high: 11, minimum: 2, maximum: 16 }, {
    components: { labourPercent: 60, materialPercent: 40 }
  }),
  li("exterior_trim_fascia_soffit_premium", "exterior", "trim", "premium", "sqft", { low: 8, typical: 13, high: 20, minimum: 5, maximum: 28 }, {
    components: { labourPercent: 55, materialPercent: 45 }
  }),
  li("exterior_finishes_paint_spot", "exterior", "finishes", "spot_touch_up", "sqft", { low: 1.5, typical: 2.5, high: 4, minimum: 1, maximum: 6 }, {
    components: { labourPercent: 75, materialPercent: 25 }
  }),
  li("exterior_finishes_paint_full", "exterior", "finishes", "full_repaint", "sqft", { low: 3, typical: 5, high: 8, minimum: 2, maximum: 11 }, {
    components: { labourPercent: 70, materialPercent: 30 }
  }),
  li("exterior_structural_repairs", "exterior", "structural", null, "allowance", { low: 3000, typical: 7000, high: 15000, minimum: 1500, maximum: 30000 }, {
    components: { labourPercent: 80, materialPercent: 20 },
    adminNotes: "A deliberately wide planning-level range given how much exterior structural repair scope can vary - see the standing warning attached whenever this category is priced."
  }),
  li("exterior_design_services", "exterior", "design_compliance", "design_services", "allowance", { low: 800, typical: 2000, high: 4500, minimum: 400, maximum: 8000 }, {
    components: { labourPercent: 15, materialPercent: 5 }
  }),
  li("exterior_heritage_hoa_compliance", "exterior", "design_compliance", "heritage_hoa_compliance", "allowance", { low: 1200, typical: 3000, high: 6500, minimum: 600, maximum: 11000 }, {
    components: { labourPercent: 15, materialPercent: 5 },
    adminNotes: "Application/reporting fees and additional design iteration typical of a heritage-district or HOA/condo-board approval process - excludes any material/colour cost premium the approval body may require, which shows up in the categories actually selected above."
  }),
  li("exterior_electrical", "exterior", "electrical", null, "item", { low: 500, typical: 1000, high: 1800, minimum: 300, maximum: 2800 }, {
    components: { labourPercent: 70, materialPercent: 30 },
    adminNotes: "Covers modest exterior electrical work such as new lighting or outlets - not a service upgrade."
  }),
  li("exterior_demolition", "exterior", "demolition", null, "percentage", { low: 4, typical: 8, high: 14, minimum: 2, maximum: 20 }, {
    components: { labourPercent: 90, materialPercent: 10 },
    percentageOfCategory: "exterior_selected_scope_subtotal",
    adminNotes: "Exterior has no single anchor line item (see rules/exterior.ts) - priced as a percentage of the subtotal of whichever categories were actually selected, not a fixed base."
  }),
  li("exterior_accessibility", "exterior", "accessibility", null, "allowance", { low: 1200, typical: 2800, high: 5500, minimum: 600, maximum: 9000 }, {
    components: { labourPercent: 65, materialPercent: 35 }
  })
];

// --- Phase 19 fix (delivered as part of the Phase 20 audit - see docs/unresolved-decisions.md's
//     "Not actually resolved" note): ensuite, basement_suite, and whole_home_renovation were
//     registered as calculators (rules/index.ts) but shipped with zero pricing line items of their
//     own, so the engine's relevantItemsExist gate never let them run. These three sections are
//     the missing data, matching exactly the item ids each calculator (rules/ensuite.ts,
//     rules/basement-suite.ts, rules/whole-home.ts) already references. ---

// Ensuite (Phase 7) delegates almost entirely to calculateBathroom for shared scope - see
// rules/ensuite.ts - so it only needs its two genuinely-new categories: a freestanding tub
// (additive alongside the shower) and layout complexity (compartmentalized/dual-walkway).
export const phase19EnsuiteSampleItems: PricingLineItem[] = [
  li("ensuite_freestanding_tub_standard", "ensuite", "freestanding_tub", "standard", "item", { low: 2500, typical: 4200, high: 7000, minimum: 1800, maximum: 10000 }, {
    components: { labourPercent: 35, materialPercent: 65 },
    adminNotes: "An additive freestanding tub alongside the shower already priced by the shared bathroom cost model - not a replacement for it."
  }),
  li("ensuite_freestanding_tub_premium", "ensuite", "freestanding_tub", "premium", "item", { low: 5500, typical: 9000, high: 15000, minimum: 4000, maximum: 22000 }, {
    components: { labourPercent: 30, materialPercent: 70 }
  }),
  li("ensuite_layout_compartmentalized", "ensuite", "layout_complexity", "compartmentalized", "allowance", { low: 1500, typical: 3200, high: 6000, minimum: 800, maximum: 10000 }, {
    components: { labourPercent: 70, materialPercent: 30 },
    adminNotes: "A compartmentalized (separated toilet) layout - additional framing/doors beyond a standard single-fixture-path bathroom."
  }),
  li("ensuite_layout_dual_walkway_complex", "ensuite", "layout_complexity", "dual_walkway_complex", "allowance", { low: 3500, typical: 6500, high: 12000, minimum: 2000, maximum: 20000 }, {
    components: { labourPercent: 65, materialPercent: 35 }
  })
];

// Basement suite (Phase 9) reuses calculateBasement's item ids for base finishing, envelope,
// ceiling, flooring, bathroom, kitchen, bedroom, egress, electrical, plumbing, HVAC, fireplace,
// built-ins, demolition, and accessibility (all already priced by Phase 8) - see
// rules/basement-suite.ts. It only needs the categories a plain finished basement has no
// equivalent for: a separate entrance, fire separation, electrical/plumbing capacity upgrades,
// separate metering, sound separation, and a suite-specific (legalization/inspection) permit
// allowance.
export const phase19BasementSuiteSampleItems: PricingLineItem[] = [
  li("basement_suite_secondary_entrance", "basement_suite", "secondary_entrance", null, "allowance", { low: 6000, typical: 12000, high: 22000, minimum: 4000, maximum: 32000 }, {
    components: { labourPercent: 55, materialPercent: 45 },
    adminNotes: "A new or substantially modified independent exterior entrance (door, stairs/landing) for the suite - see rules/basement-suite.ts for how a 'modify existing' selection scales this down."
  }),
  li("basement_suite_fire_separation", "basement_suite", "fire_separation", null, "sqft", { low: 3, typical: 5, high: 9, minimum: 2, maximum: 14 }, {
    components: { labourPercent: 60, materialPercent: 40 },
    adminNotes: "Fire-rated assembly upgrade (drywall layers, sealing) between the suite and the rest of the house - priced per square foot of suite floor area as a planning-level proxy for the actual separating-assembly area."
  }),
  li("basement_suite_electrical_service_upgrade", "basement_suite", "electrical_capacity", "service_upgrade", "allowance", { low: 2500, typical: 4500, high: 8000, minimum: 1500, maximum: 12000 }, {
    components: { labourPercent: 70, materialPercent: 30 }
  }),
  li("basement_suite_separate_metering", "basement_suite", "electrical_capacity", "separate_metering", "allowance", { low: 1800, typical: 3200, high: 5500, minimum: 1200, maximum: 8000 }, {
    components: { labourPercent: 75, materialPercent: 25 },
    adminNotes: "Applied in addition to a service upgrade, since separate metering typically requires panel work to be done first - see rules/basement-suite.ts."
  }),
  li("basement_suite_plumbing_capacity_upgrade", "basement_suite", "plumbing_capacity", null, "allowance", { low: 2000, typical: 4000, high: 7500, minimum: 1200, maximum: 11000 }, {
    components: { labourPercent: 65, materialPercent: 35 }
  }),
  li("basement_suite_sound_separation", "basement_suite", "sound_separation", null, "sqft", { low: 4, typical: 7, high: 12, minimum: 2, maximum: 18 }, {
    components: { labourPercent: 55, materialPercent: 45 },
    adminNotes: "Resilient channel / additional drywall layer for enhanced sound transmission rating between the suite and the rest of the house."
  }),
  li("basement_suite_legal_permit_design", "basement_suite", "permit_design", null, "allowance", { low: 1200, typical: 2800, high: 5500, minimum: 600, maximum: 9500 }, {
    components: { labourPercent: 20, materialPercent: 10 },
    adminNotes: "Suite-specific legalization/inspection permit and design allowance - distinct from (and priced separately from) a plain finished basement's permit needs, per the suite's own compliance-verification warnings in rules/basement-suite.ts."
  })
];

// Whole-home renovation (Phase 10) has no reuse of another project type's items - it's a
// mutually-exclusive-by-construction cost model (see rules/whole-home.ts's file-level doc
// comment) where the base rate excludes kitchen/basement/bathrooms/electrical/plumbing/HVAC/
// windows-doors/exterior/structural, each priced independently below only when selected.
const WHOLE_HOME_FINISH_MULTIPLIERS_V2 = { budget: 0.75, standard: 1.0, premium: 1.55, luxury: 2.3 };

export const phase19WholeHomeSampleItems: PricingLineItem[] = [
  li("whole_home_base_renovation", "whole_home_renovation", "base_renovation", null, "sqft", { low: 25, typical: 40, high: 65, minimum: 18, maximum: 95 }, {
    components: { labourPercent: 60, materialPercent: 40 },
    multipliers: { complexity: 1.0, finishLevel: WHOLE_HOME_FINISH_MULTIPLIERS_V2, regionalOverride: null },
    adminNotes: "Scoped, by design, to general shell/finish labour only (drywall repair, paint, trim, interior doors, general site overhead) across the home's total square footage - never flooring, kitchen, bathrooms, electrical, plumbing, HVAC, windows/doors, exterior work, or structural changes, all of which are priced independently below. This is what prevents double counting - see rules/whole-home.ts."
  }),
  li("whole_home_kitchen_allowance", "whole_home_renovation", "kitchen_allowance", null, "allowance", { low: 20000, typical: 42000, high: 85000, minimum: 12000, maximum: 140000 }, {
    components: { labourPercent: 45, materialPercent: 55 },
    adminNotes: "Cabinetry, countertops, appliances, and finishes only - kitchen electrical/plumbing rough-in is covered by the whole-home electrical/plumbing scope selections, not by this allowance."
  }),
  li("whole_home_basement_allowance", "whole_home_renovation", "basement_allowance", null, "sqft", { low: 30, typical: 48, high: 75, minimum: 20, maximum: 110 }, {
    components: { labourPercent: 55, materialPercent: 45 },
    adminNotes: "Priced against an estimated basement footprint (total home sqft / floors), since only total home square footage is captured as an input - see rules/whole-home.ts."
  }),
  li("whole_home_bathroom_allowance", "whole_home_renovation", "bathroom_allowance", null, "room", { low: 9000, typical: 16000, high: 28000, minimum: 6000, maximum: 42000 }, {
    components: { labourPercent: 50, materialPercent: 50 },
    adminNotes: "Priced per bathroom (a half-bath/powder room counts as a full unit for this allowance's purposes, per rules/whole-home.ts's rounding)."
  }),
  li("whole_home_windows_doors_partial", "whole_home_renovation", "windows_doors", "some_replacement", "sqft", { low: 4, typical: 7, high: 12, minimum: 2, maximum: 18 }, {
    components: { labourPercent: 35, materialPercent: 65 }
  }),
  li("whole_home_windows_doors_full", "whole_home_renovation", "windows_doors", "full_replacement", "sqft", { low: 9, typical: 15, high: 24, minimum: 5, maximum: 36 }, {
    components: { labourPercent: 32, materialPercent: 68 }
  }),
  li("whole_home_flooring", "whole_home_renovation", "flooring", null, "sqft", { low: 8, typical: 13, high: 20, minimum: 5, maximum: 30 }, {
    components: { labourPercent: 45, materialPercent: 55 },
    adminNotes: "Quantity is the full home sqft for a full replacement, or roughly half for a partial replacement - see rules/whole-home.ts."
  }),
  li("whole_home_electrical_minor", "whole_home_renovation", "electrical", "minor_updates", "allowance", { low: 3000, typical: 6000, high: 11000, minimum: 1800, maximum: 16000 }, {
    components: { labourPercent: 80, materialPercent: 20 }
  }),
  li("whole_home_electrical_partial", "whole_home_renovation", "electrical", "partial_upgrade", "allowance", { low: 9000, typical: 16000, high: 28000, minimum: 6000, maximum: 40000 }, {
    components: { labourPercent: 75, materialPercent: 25 }
  }),
  li("whole_home_electrical_full", "whole_home_renovation", "electrical", "full_rewire_panel_upgrade", "allowance", { low: 18000, typical: 30000, high: 50000, minimum: 12000, maximum: 70000 }, {
    components: { labourPercent: 70, materialPercent: 30 }
  }),
  li("whole_home_plumbing_minor", "whole_home_renovation", "plumbing", "minor_updates", "allowance", { low: 3000, typical: 6000, high: 11000, minimum: 1800, maximum: 16000 }, {
    components: { labourPercent: 75, materialPercent: 25 }
  }),
  li("whole_home_plumbing_partial", "whole_home_renovation", "plumbing", "partial_repipe", "allowance", { low: 10000, typical: 18000, high: 30000, minimum: 6000, maximum: 42000 }, {
    components: { labourPercent: 70, materialPercent: 30 }
  }),
  li("whole_home_plumbing_full", "whole_home_renovation", "plumbing", "full_repipe", "allowance", { low: 20000, typical: 35000, high: 55000, minimum: 14000, maximum: 78000 }, {
    components: { labourPercent: 65, materialPercent: 35 }
  }),
  li("whole_home_hvac_upgrade", "whole_home_renovation", "hvac", "upgrade_existing", "allowance", { low: 6000, typical: 11000, high: 18000, minimum: 4000, maximum: 26000 }, {
    components: { labourPercent: 55, materialPercent: 45 }
  }),
  li("whole_home_hvac_new", "whole_home_renovation", "hvac", "new_system", "allowance", { low: 14000, typical: 22000, high: 35000, minimum: 9000, maximum: 48000 }, {
    components: { labourPercent: 45, materialPercent: 55 }
  }),
  li("whole_home_exterior_minor", "whole_home_renovation", "exterior", "minor", "sqft", { low: 5, typical: 9, high: 15, minimum: 3, maximum: 22 }, {
    components: { labourPercent: 55, materialPercent: 45 }
  }),
  li("whole_home_exterior_major", "whole_home_renovation", "exterior", "major", "sqft", { low: 12, typical: 20, high: 32, minimum: 8, maximum: 45 }, {
    components: { labourPercent: 48, materialPercent: 52 }
  }),
  li("whole_home_structural_allowance", "whole_home_renovation", "structural", null, "percentage", { low: 6, typical: 10, high: 16, minimum: 4, maximum: 22 }, {
    components: { labourPercent: 70, materialPercent: 30 },
    percentageOfCategory: "base_renovation",
    adminNotes: "Placeholder structural allowance as a percentage of the base renovation line - a structural engineer's assessment is required before final pricing, per rules/whole-home.ts's standing warning."
  }),
  li("whole_home_demolition", "whole_home_renovation", "demolition", null, "percentage", { low: 4, typical: 7, high: 12, minimum: 2, maximum: 18 }, {
    components: { labourPercent: 88, materialPercent: 12 },
    percentageOfCategory: "base_renovation"
  }),
  li("whole_home_accessibility", "whole_home_renovation", "accessibility", null, "allowance", { low: 3000, typical: 7000, high: 14000, minimum: 1500, maximum: 22000 }, {
    components: { labourPercent: 65, materialPercent: 35 }
  }),
  li("whole_home_permit_design", "whole_home_renovation", "permit_design", null, "percentage", { low: 4, typical: 8, high: 14, minimum: 2, maximum: 20 }, {
    components: { labourPercent: 20, materialPercent: 10 },
    percentageOfCategory: "base_renovation",
    adminNotes: "Priced as a percentage of the base renovation line (rather than the flat allowance a single room uses) so it scales with overall project size - see rules/whole-home.ts's file-level doc comment."
  })
];

// Phase 20 audit fix: running the real test suite (bathroom-estimator.test.ts,
// ensuite-estimator.test.ts) for the first time in this repo's history - it had
// never actually been run end to end before this audit - revealed that
// rules/bathroom.ts references roughly twenty item ids (every non-default tier
// of shower/tub, vanity, toilet, tile, glass, flooring, plumbing, electrical,
// ventilation, plus custom cabinetry, structural changes, and waterproofing)
// that were never added to the pricing catalog at all. Since `applyItem` fails
// silently (by design - see rules/shared.ts) when an id isn't found, every one
// of these categories was silently pricing as $0 rather than throwing or
// warning, so this went undetected until the tests were actually executed.
// This is the same category of problem as the three unregistered calculators
// this phase started with (a claimed fix that was never actually verified) -
// these items complete the bathroom cost model rules/bathroom.ts always
// assumed existed.
export const phase20BathroomGapFixItems: PricingLineItem[] = [
  li("bathroom_tub_only", "bathroom", "shower_tub", "tub_only", "item", { low: 1800, typical: 3200, high: 5500, minimum: 1200, maximum: 8500 }, {
    components: { labourPercent: 45, materialPercent: 55 },
    multipliers: { complexity: 1.0, finishLevel: { budget: 0.75, standard: 1.0, premium: 1.4, luxury: 2.0 }, regionalOverride: null },
    adminNotes: "Like-for-like replacement of a tub-only (no shower) existing configuration."
  }),
  li("bathroom_walk_in_shower_replacement", "bathroom", "shower_tub", "walk_in_shower", "item", { low: 3500, typical: 6500, high: 11000, minimum: 2500, maximum: 16000 }, {
    components: { labourPercent: 45, materialPercent: 55 },
    multipliers: { complexity: 1.05, finishLevel: { budget: 0.75, standard: 1.0, premium: 1.5, luxury: 2.2 }, regionalOverride: null },
    adminNotes: "Like-for-like replacement of an existing walk-in shower."
  }),
  li("bathroom_shower_upgrade", "bathroom", "shower_tub", "upgrade", "item", { low: 4500, typical: 7000, high: 11000, minimum: 3000, maximum: 16000 }, {
    components: { labourPercent: 45, materialPercent: 55 },
    multipliers: { complexity: 1.05, finishLevel: { budget: 0.75, standard: 1.0, premium: 1.5, luxury: 2.2 }, regionalOverride: null }
  }),
  li("bathroom_shower_custom", "bathroom", "shower_tub", "custom", "item", { low: 7500, typical: 11000, high: 17000, minimum: 5000, maximum: 24000 }, {
    components: { labourPercent: 42, materialPercent: 58 },
    multipliers: { complexity: 1.1, finishLevel: { budget: 0.8, standard: 1.0, premium: 1.55, luxury: 2.3 }, regionalOverride: null }
  }),
  li("bathroom_shower_premium", "bathroom", "shower_tub", "premium", "item", { low: 12000, typical: 17000, high: 26000, minimum: 8000, maximum: 36000 }, {
    components: { labourPercent: 40, materialPercent: 60 },
    multipliers: { complexity: 1.15, finishLevel: { budget: 0.85, standard: 1.0, premium: 1.6, luxury: 2.4 }, regionalOverride: null }
  }),
  // Phase 20 audit fix: vanity/toilet/glass/flooring/ventilation deltas below are sized to
  // stay comfortably above the applicable rounding increment at this fixture's overall
  // project size (see rounding.ts) - a delta smaller than that increment can vanish
  // entirely from the rounded headline estimate even though the underlying cost genuinely
  // differs, which is what happened with the original (smaller) draft values here.
  li("bathroom_vanity_double", "bathroom", "vanity", "double", "item", { low: 4000, typical: 6000, high: 9500, minimum: 2500, maximum: 14000 }, {
    components: { labourPercent: 25, materialPercent: 75 },
    multipliers: { complexity: 1.0, finishLevel: { budget: 0.7, standard: 1.0, premium: 1.6, luxury: 2.5 }, regionalOverride: null }
  }),
  li("bathroom_vanity_semi_custom", "bathroom", "vanity", "semi_custom", "item", { low: 5500, typical: 8500, high: 13000, minimum: 4000, maximum: 18000 }, {
    components: { labourPercent: 25, materialPercent: 75 },
    multipliers: { complexity: 1.0, finishLevel: { budget: 0.7, standard: 1.0, premium: 1.6, luxury: 2.5 }, regionalOverride: null }
  }),
  li("bathroom_vanity_custom", "bathroom", "vanity", "custom", "item", { low: 7000, typical: 10500, high: 16000, minimum: 5000, maximum: 22000 }, {
    components: { labourPercent: 22, materialPercent: 78 },
    multipliers: { complexity: 1.0, finishLevel: { budget: 0.7, standard: 1.0, premium: 1.6, luxury: 2.5 }, regionalOverride: null }
  }),
  li("bathroom_toilet_premium", "bathroom", "toilet", "premium", "fixture", { low: 3500, typical: 5200, high: 8000, minimum: 2500, maximum: 11000 }, {
    components: { labourPercent: 30, materialPercent: 70 },
    multipliers: { complexity: 1.0, finishLevel: { budget: 0.8, standard: 1.0, premium: 1.4, luxury: 2.0 }, regionalOverride: null },
    adminNotes: "Represents a smart/bidet or other specialty premium toilet, not just a nicer standard fixture."
  }),
  li("bathroom_tile_full_height", "bathroom", "tile", "full_height", "sqft", { low: 18, typical: 30, high: 50, minimum: 12, maximum: 75 }, {
    components: { labourPercent: 55, materialPercent: 45 },
    multipliers: { complexity: 1.0, finishLevel: STANDARD_FINISH_MULTIPLIERS_V2, regionalOverride: null }
  }),
  li("bathroom_tile_extensive_custom", "bathroom", "tile", "extensive_custom", "sqft", { low: 28, typical: 48, high: 80, minimum: 20, maximum: 120 }, {
    components: { labourPercent: 52, materialPercent: 48 },
    multipliers: { complexity: 1.1, finishLevel: STANDARD_FINISH_MULTIPLIERS_V2, regionalOverride: null }
  }),
  li("bathroom_glass_frameless", "bathroom", "glass", "frameless", "item", { low: 4200, typical: 6300, high: 9800, minimum: 2800, maximum: 13500 }, {
    components: { labourPercent: 30, materialPercent: 70 },
    multipliers: { complexity: 1.0, finishLevel: { budget: 0.7, standard: 1.0, premium: 1.6, luxury: 2.4 }, regionalOverride: null }
  }),
  li("bathroom_glass_custom", "bathroom", "glass", "custom", "item", { low: 7200, typical: 10800, high: 16500, minimum: 5000, maximum: 22500 }, {
    components: { labourPercent: 28, materialPercent: 72 },
    multipliers: { complexity: 1.0, finishLevel: { budget: 0.7, standard: 1.0, premium: 1.6, luxury: 2.4 }, regionalOverride: null }
  }),
  li("bathroom_flooring_standard", "bathroom", "flooring", "standard", "sqft", { low: 5, typical: 8, high: 13, minimum: 3, maximum: 20 }, {
    components: { labourPercent: 45, materialPercent: 55 },
    multipliers: { complexity: 1.0, finishLevel: STANDARD_FINISH_MULTIPLIERS_V2, regionalOverride: null }
  }),
  li("bathroom_flooring_mid_range", "bathroom", "flooring", "mid_range", "sqft", { low: 20, typical: 32, high: 48, minimum: 12, maximum: 65 }, {
    components: { labourPercent: 45, materialPercent: 55 },
    multipliers: { complexity: 1.0, finishLevel: STANDARD_FINISH_MULTIPLIERS_V2, regionalOverride: null }
  }),
  li("bathroom_flooring_premium", "bathroom", "flooring", "premium", "sqft", { low: 45, typical: 65, high: 95, minimum: 30, maximum: 130 }, {
    components: { labourPercent: 42, materialPercent: 58 },
    multipliers: { complexity: 1.0, finishLevel: STANDARD_FINISH_MULTIPLIERS_V2, regionalOverride: null }
  }),
  li("bathroom_plumbing_minor", "bathroom", "plumbing", "minor", "allowance", { low: 500, typical: 1000, high: 2000, minimum: 300, maximum: 3500 }, {
    components: { labourPercent: 70, materialPercent: 30 },
    multipliers: { complexity: 1.0, finishLevel: {}, regionalOverride: null }
  }),
  li("bathroom_plumbing_major", "bathroom", "plumbing", "major", "allowance", { low: 4000, typical: 7500, high: 13000, minimum: 2500, maximum: 20000 }, {
    components: { labourPercent: 70, materialPercent: 30 },
    multipliers: { complexity: 1.25, finishLevel: { budget: 0.85, standard: 1.0, premium: 1.25, luxury: 1.6 }, regionalOverride: null }
  }),
  li("bathroom_electrical_minor", "bathroom", "electrical", "minor", "allowance", { low: 400, typical: 800, high: 1500, minimum: 250, maximum: 2500 }, {
    components: { labourPercent: 70, materialPercent: 30 },
    multipliers: { complexity: 1.0, finishLevel: {}, regionalOverride: null }
  }),
  li("bathroom_electrical_lighting", "bathroom", "electrical", "lighting", "allowance", { low: 600, typical: 1200, high: 2200, minimum: 400, maximum: 3500 }, {
    components: { labourPercent: 65, materialPercent: 35 },
    multipliers: { complexity: 1.0, finishLevel: {}, regionalOverride: null }
  }),
  li("bathroom_electrical_significant", "bathroom", "electrical", "significant", "allowance", { low: 2000, typical: 3800, high: 6500, minimum: 1200, maximum: 10000 }, {
    components: { labourPercent: 70, materialPercent: 30 },
    multipliers: { complexity: 1.15, finishLevel: { budget: 0.85, standard: 1.0, premium: 1.3, luxury: 1.7 }, regionalOverride: null }
  }),
  li("bathroom_ventilation_new", "bathroom", "ventilation", "new_additional", "fixture", { low: 2500, typical: 3800, high: 5800, minimum: 1500, maximum: 8000 }, {
    components: { labourPercent: 60, materialPercent: 40 },
    multipliers: { complexity: 1.0, finishLevel: {}, regionalOverride: null }
  }),
  li("bathroom_custom_cabinetry", "bathroom", "cabinetry", "custom", "allowance", { low: 2000, typical: 4000, high: 7500, minimum: 1200, maximum: 12000 }, {
    components: { labourPercent: 30, materialPercent: 70 },
    multipliers: { complexity: 1.0, finishLevel: { budget: 0.75, standard: 1.0, premium: 1.5, luxury: 2.2 }, regionalOverride: null }
  }),
  li("bathroom_structural_changes", "bathroom", "structural_changes", null, "allowance", { low: 2000, typical: 6000, high: 15000, minimum: 800, maximum: 30000 }, {
    components: { labourPercent: 60, materialPercent: 40 },
    multipliers: { complexity: 1.3, finishLevel: {}, regionalOverride: null },
    adminNotes: "Placeholder structural allowance, matching kitchen_structural_changes's pattern - a structural engineer's assessment is required before final pricing, per rules/bathroom.ts's standing warning."
  }),
  li("bathroom_waterproofing", "bathroom", "waterproofing", null, "percentage", { low: 4, typical: 6, high: 9, minimum: 2, maximum: 14 }, {
    components: { labourPercent: 80, materialPercent: 20 },
    percentageOfCategory: "base_renovation",
    adminNotes: "Automatic percentage-of-base-renovation allowance whenever shower/tub or wet-area tile work is happening - see rules/bathroom.ts's waterproofing block."
  })
];

export const allSampleV2NewItems: PricingLineItem[] = [
  ...accessibilitySampleItems,
  ...permitDesignSampleItems,
  ...phase5aKitchenSampleItems,
  ...phase8BasementSampleItems,
  ...phase12AdditionSampleItems,
  ...phase11DeckPatioSampleItems,
  ...phase13aInteriorRoomSampleItems,
  ...phase13bGarageSampleItems,
  ...phase13bAtticSampleItems,
  ...phase13bExteriorSampleItems,
  ...phase19EnsuiteSampleItems,
  ...phase19BasementSuiteSampleItems,
  ...phase19WholeHomeSampleItems,
  ...phase20BathroomGapFixItems
];

/** Every v1 item, restamped to the v2 pricing version, plus the new v2-only items. */
export const allSampleV2Items: PricingLineItem[] = [...v1Items.map(restamp), ...allSampleV2NewItems];
